import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, TabStopPosition, TabStopType } from 'docx';
import { saveAs } from 'file-saver';
// import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import BragSheetProgressIndicator from './BragSheetProgressIndicator';
import ProfessionalBragSheetPDF from './ProfessionalBragSheetPDF';
import ProfessionalBragSheetView from './ProfessionalBragSheetView';
import ShareButton from '../Sharing/ShareButton';
import ThematicReviewModal from './ThematicReviewModal';
import {
  FileText,
  Download,
  Plus,
  Trash2,
  Edit3,
  Star,
  Award,
  BookOpen,
  Users,
  Heart,
  Briefcase,
  Target,
  Save,
  Eye,
  EyeOff,
  Sparkles,
  RefreshCw,
  ChevronDown,
  X,
  Clock,
  Share2,
  MoreHorizontal,
  GraduationCap,
  Calendar,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

// Compact Brag Sheet Preview Component for Sidebar
const CompactBragSheetPreview = ({ bragSheetData }) => {
  const { 
    basicInfo = {}, 
    academicHighlights = {}, 
    extracurriculars = [], 
    achievements = [],
    personalQualities = [],
    leadershipContexts = {}
    // characterStories = []  // Will be used in future enhancements
  } = bragSheetData;

  // Calculate completion percentage for visual feedback
  const calculateCompletion = () => {
    let total = 0;
    let completed = 0;
    
    // Basic info fields
    ['name', 'gradeLevel', 'school', 'intendedMajor'].forEach(field => {
      total++;
      if (basicInfo[field]) completed++;
    });
    
    // Academic fields
    ['gpa', 'classRank', 'testScores'].forEach(field => {
      total++;
      if (academicHighlights[field]) completed++;
    });
    
    // Lists
    total += 3; // For having at least one of each
    if (extracurriculars.length > 0) completed++;
    if (achievements.length > 0) completed++;
    if (personalQualities.length > 0 || Object.keys(leadershipContexts).length > 0) completed++;
    
    return Math.round((completed / total) * 100);
  };

  const completionPercent = calculateCompletion();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-stone-900 mb-2">Brag Sheet Preview</h3>
        <div className="w-full bg-stone-100 rounded-full h-2 mb-4">
          <div 
            className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercent}%` }}
          />
        </div>
        <p className="text-xs text-stone-500">{completionPercent}% Complete</p>
      </div>

      {/* Student Name & Basic Info */}
      <div className="border-l-4 border-azure-500 pl-3 py-2">
        <h4 className="font-semibold text-stone-900">
          {basicInfo.name || 'Student Name'}
        </h4>
        <p className="text-sm text-stone-600">
          {basicInfo.gradeLevel || 'Grade'} • {basicInfo.school || 'School Name'}
        </p>
        {basicInfo.intendedMajor && (
          <p className="text-xs text-stone-500 mt-1">
            Intended: {basicInfo.intendedMajor}
          </p>
        )}
      </div>

      {/* Academic Highlights */}
      {(academicHighlights.gpa || academicHighlights.classRank || academicHighlights.testScores) && (
        <div className="bg-stone-50 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
            Academic Profile
          </h5>
          <div className="space-y-1">
            {academicHighlights.gpa && (
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">GPA</span>
                <span className="font-medium">{academicHighlights.gpa}</span>
              </div>
            )}
            {academicHighlights.classRank && (
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">Rank</span>
                <span className="font-medium">{academicHighlights.classRank}</span>
              </div>
            )}
            {academicHighlights.testScores && (
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">Test</span>
                <span className="font-medium">{academicHighlights.testScores}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Activities */}
      {extracurriculars.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
            Top Activities ({extracurriculars.length})
          </h5>
          <div className="space-y-2">
            {extracurriculars.slice(0, 3).map((activity, idx) => (
              <div key={idx} className="text-sm border-l-2 border-stone-200 pl-2">
                <div className="font-medium text-stone-800">{activity.activity}</div>
                {activity.role && (
                  <div className="text-xs text-stone-500">{activity.role}</div>
                )}
              </div>
            ))}
            {extracurriculars.length > 3 && (
              <p className="text-xs text-stone-400 italic">
                +{extracurriculars.length - 3} more activities
              </p>
            )}
          </div>
        </div>
      )}

      {/* Key Achievements */}
      {achievements.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
            Achievements ({achievements.length})
          </h5>
          <div className="space-y-1">
            {achievements.slice(0, 2).map((achievement, idx) => (
              <div key={idx} className="text-sm flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">★</span>
                <span className="text-stone-700 leading-tight">{achievement.title}</span>
              </div>
            ))}
            {achievements.length > 2 && (
              <p className="text-xs text-stone-400 italic pl-6">
                +{achievements.length - 2} more
              </p>
            )}
          </div>
        </div>
      )}

      {/* Leadership Contexts */}
      {Object.keys(leadershipContexts).length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
            Leadership Stories
          </h5>
          <div className="flex flex-wrap gap-1">
            {Object.entries(leadershipContexts).map(([contextId, description]) => {
              if (!description || !description.trim()) return null;
              const contextLabel = contextId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <span
                  key={contextId}
                  className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full"
                  title={description.substring(0, 100) + (description.length > 100 ? '...' : '')}
                >
                  {contextLabel}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Personal Qualities */}
      {personalQualities.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
            Key Qualities
          </h5>
          <div className="flex flex-wrap gap-1">
            {personalQualities.slice(0, 4).map((quality, idx) => (
              <span 
                key={idx} 
                className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full"
              >
                {typeof quality === 'string' ? quality : quality.quality}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Export Preview Button */}
      <div className="pt-4 border-t border-stone-200">
        <button
          onClick={() => {
            const previewButton = document.querySelector('[data-preview-button]');
            if (previewButton) previewButton.click();
          }}
          className="w-full px-3 py-2 bg-stone-900 text-white text-sm rounded-lg hover:bg-stone-800 transition-colors"
        >
          View Full Preview →
        </button>
      </div>
    </div>
  );
};

/**
 * Comprehensive Brag Sheet Framework
 * Integrates with student academic profiles and assessment data
 * Provides export capabilities for recommendation letters
 */
export default function BragSheetFramework({ isTeacherSpecific = false }) {
  const { currentUser } = useFamilyContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for teacher-specific mode from navigation state
  const teacherSpecificFromState = location.state?.teacherSpecific || false;
  const previewMode = location.state?.previewMode || false;
  const showExport = location.state?.showExport || false;
  const teacherInfo = location.state?.teacherInfo || {};
  const recommendationId = location.state?.recommendationId || null;
  const showProgressBar = location.state?.showProgressBar || false;
  const fromRecommendationsHub = location.state?.fromRecommendationsHub || false;
  const initialSelectedTeacherId = location.state?.selectedTeacherId || teacherInfo?.id || null;

  // Use either prop or state to determine teacher-specific mode
  const isTeacherMode = isTeacherSpecific || teacherSpecificFromState;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [profileId, setProfileId] = useState(null); // Track existing profile ID to prevent data loss
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);
  const [autoPopulated, setAutoPopulated] = useState(false);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState(new Set());
  const [bragSheetData, setBragSheetData] = useState({
    basicInfo: {
      name: '',
      gradeLevel: '',
      graduationYear: '',
      school: '',
      intendedMajor: '',
      email: '',
      phone: '',
      location: ''
    },
    academicHighlights: {
      gpa: '',
      classRank: '',
      courseLoad: '',
      academicChallenges: ''
    },
    achievements: [],        // Simplified to title + year format
    extracurriculars: [],
    recommendationTopics: [],
    leadershipContexts: {},  // For Personal tab
    personalBrand: '',       // "In My Own Words" section

    // Teacher-specific fields
    collegeGoals: '',        // Moved to Overview tab
    classroomMoments: [],    // Teacher-specific tab
    unexpectedDetails: [],   // Moved to Personal tab
    thankYouNote: '',        // Teacher-specific tab

    // Section exclusions for customization
    sectionExclusions: {
      collegeGoals: false,
      achievements: false,
      classroomMoments: false,
      leadershipContexts: false,
      unexpectedDetails: false,
      thankYouNote: false,
      personalBrand: false
    }
  });
  const [activeTab, setActiveTab] = useState(isTeacherMode ? 'teacher-specific' : 'overview');
  // const [previewMode, setPreviewMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sidebarView, setSidebarView] = useState('preview'); // 'preview' or 'progress'
  const [excludePersonalBrand, setExcludePersonalBrand] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(initialSelectedTeacherId);
  const [teachers, setTeachers] = useState([]);
  const [completionScore, setCompletionScore] = useState(0);
  const [workflowState, setWorkflowState] = useState('not_started');
  const [showThematicReview, setShowThematicReview] = useState(false);
  const [thematicAnalysis, setThematicAnalysis] = useState(null);
  const [analyzingTheme, setAnalyzingTheme] = useState(false);
  const printRef = useRef();

  // Load existing data on component mount
  useEffect(() => {
    if (currentUser?.id) {
      loadStudentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // Auto-select teacher and switch tab if coming from recommendations hub
  useEffect(() => {
    if (initialSelectedTeacherId && showProgressBar) {
      setSelectedTeacherId(initialSelectedTeacherId);
      setActiveTab('teacher-specific');
    }
  }, [initialSelectedTeacherId, showProgressBar]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load teachers/recommenders for dropdown
  useEffect(() => {
    const loadTeachers = async () => {
      if (!currentUser?.id) {
        console.log('📚 BragSheet: No current user, skipping teacher load');
        return;
      }

      try {
        console.log('📚 BragSheet: Loading teachers for student:', currentUser.id);
        const { data, error } = await supabase
          .from('college_recommendation_letters')
          .select('id, recommender_name, subject, recommender_email, status')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('❌ Error fetching teachers:', error);
          return;
        }

        console.log('✅ Teachers loaded:', data?.length || 0, data);
        setTeachers(data || []);
      } catch (error) {
        console.error('❌ Error loading teachers:', error);
      }
    };

    loadTeachers();
  }, [currentUser?.id]);

  const loadStudentData = async () => {
    try {
      setLoading(true);
      
      // Fetch student academic profile, assessment data, and activities
      const [profileResult, assessmentResult, familyResult, activitiesResult] = await Promise.all([
        supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false }),
        
        supabase
          .from('assessment_responses')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single(),
        
        supabase
          .from('family_members')
          .select('*')
          .eq('id', currentUser.id)
          .single(),
          
        supabase
          .from('student_activities')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('years_participated', { ascending: false })
      ]);

      const profiles = profileResult.data || [];
      const assessment = assessmentResult.data || {};
      const family = familyResult.data || {};
      const activities = activitiesResult.data || [];

      // Find the primary profile (one with most academic data, not just brag sheet)
      let primaryProfile = null;
      if (profiles.length > 0) {
        console.log('🔍 Found', profiles.length, 'profile(s) for user');
        
        // Prioritize profiles with substantial academic data over brag sheet-only profiles
        primaryProfile = profiles.find(p => 
          p.gpa_weighted || p.gpa_unweighted || p.sat_total || p.act_composite || p.class_rank
        ) || profiles[0]; // Fallback to most recent if none have academic data
        
        console.log('📍 Selected profile ID:', primaryProfile.id);
        console.log('📊 Profile has academic data:', !!(primaryProfile.gpa_weighted || primaryProfile.sat_total));
        
        setProfileId(primaryProfile.id);
      } else {
        console.log('⚠️ No profiles found for user');
      }

      const profile = primaryProfile || {};

      setStudentData({ profile, assessment, family, activities });

      // Load cached thematic analysis if available
      if (profile.thematic_analysis_results) {
        console.log('📊 Loaded cached thematic analysis from', profile.thematic_analysis_date);
        setThematicAnalysis(profile.thematic_analysis_results);
      }

      // Auto-populate brag sheet with comprehensive data
      const populatedData = generateComprehensiveBragSheet(profile, assessment, family, activities, excludePersonalBrand);
      
      // Track which fields were auto-populated
      const populatedFields = new Set();
      if (populatedData.basicInfo.name) populatedFields.add('name');
      if (populatedData.basicInfo.gradeLevel) populatedFields.add('gradeLevel');
      if (populatedData.basicInfo.school) populatedFields.add('school');
      if (populatedData.basicInfo.intendedMajor) populatedFields.add('intendedMajor');
      if (populatedData.basicInfo.careerGoals) populatedFields.add('careerGoals');
      if (populatedData.academicHighlights.gpa) populatedFields.add('gpa');
      if (populatedData.academicHighlights.classRank) populatedFields.add('classRank');
      if (populatedData.academicHighlights.courseLoad) populatedFields.add('courseLoad');
      if (populatedData.achievements.length > 0) populatedFields.add('achievements');
      if (populatedData.extracurriculars.length > 0) populatedFields.add('extracurriculars');
      if (populatedData.personalQualities.length > 0) populatedFields.add('personalQualities');
      if (populatedData.challengesOvercome.length > 0) populatedFields.add('challenges');
      
      setAutoPopulatedFields(populatedFields);
      setAutoPopulated(true);
      
      // Merge with any existing saved brag sheet data
      setBragSheetData(() => ({
        ...populatedData,
        // Preserve any existing manual entries
        ...(profile.brag_sheet_data || {})
      }));

    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Comprehensive auto-population function
  const generateComprehensiveBragSheet = (profile, assessment, family, activities, excludePersonalBrand = false) => {
    // Extract insights from assessment if available
    const insights = extractAssessmentInsights(assessment);
    
    // Generate achievements from multiple sources
    const achievements = generateAchievements(profile, activities);
    
    // Convert activities to extracurricular format
    const extracurriculars = formatActivitiesAsExtracurriculars(activities, profile);
    
    // Extract personal qualities from assessment insights
    const personalQualities = extractPersonalQualities(insights, profile, excludePersonalBrand);
    
    // Generate challenges overcome from grade trends and context
    const challengesOvercome = extractChallengesOvercome(profile);
    
    return {
      basicInfo: {
        name: family.name || profile.name || '',
        gradeLevel: profile.grade_level || family.grade || '',
        school: profile.high_school_name || profile.school || '',
        intendedMajor: profile.intended_major || profile.academic_interests || '',
        careerGoals: profile.career_goals || profile.career_interests || ''
      },
      academicHighlights: {
        gpa: formatGPA(profile),
        classRank: formatClassRank(profile),
        courseLoad: formatCourseLoad(profile),
        academicChallenges: generateAcademicChallenges(profile),
        favoriteSubjects: extractFavoriteSubjects(profile)
      },
      achievements,
      extracurriculars,
      personalQualities,
      challengesOvercome,
      recommendationTopics: []
    };
  };

  // Helper function to extract assessment insights
  const extractAssessmentInsights = (assessment) => {
    if (!assessment?.formatted_insights?.insights) return null;
    
    const insights = assessment.formatted_insights.insights;
    return {
      superpowers: insights.hiddenSuperpowers?.superpowers || [],
      positioning: insights.strategicPositioning || {},
      essayThemes: insights.essayStrategy || {},
      tacticalAdvantages: insights.tacticalAdvantages || {},
      uniqueTraits: insights.uniqueTraits || []
    };
  };

  // Helper function to generate achievements
  const generateAchievements = (profile, activities) => {
    const achievements = [];
    
    // Add National Merit status if applicable
    if (profile.national_merit_status && profile.national_merit_status !== 'none') {
      achievements.push({
        id: 'nm-' + Date.now(),
        title: `National Merit ${profile.national_merit_status}`,
        category: 'academic',
        description: 'Recognized for exceptional performance on the PSAT/NMSQT',
        impact: 'Top 1% of test takers nationally',
        date: profile.psat_date || ''
      });
    }
    
    // Parse and add academic awards
    if (profile.academic_awards) {
      const awards = Array.isArray(profile.academic_awards)
        ? profile.academic_awards
        : typeof profile.academic_awards === 'string' 
          ? profile.academic_awards.split(',').map(a => a.trim())
          : [];
        
      awards.forEach((award, index) => {
        if (award) {
          achievements.push({
            id: 'award-' + index,
            title: award,
            category: 'academic',
            description: '',
            impact: '',
            date: ''
          });
        }
      });
    }
    
    // Add achievements from activities
    activities.forEach(activity => {
      if (activity.achievements) {
        achievements.push({
          id: 'activity-' + activity.id,
          title: `${activity.activity_name} Achievement`,
          category: activity.leadership_role ? 'leadership' : 'other',
          description: activity.achievements,
          impact: activity.leadership_role ? 'Leadership role demonstrating impact' : '',
          date: ''
        });
      }
    });
    
    return achievements;
  };

  // Helper function to format activities as extracurriculars
  const formatActivitiesAsExtracurriculars = (activities, profile) => {
    const extracurriculars = [];
    
    // Add from student_activities table
    if (activities && activities.length > 0) {
      activities.forEach(activity => {
        extracurriculars.push({
          id: activity.id,
          activity: activity.activity_name,
          role: activity.leadership_role ? 'Leadership Role' : 'Member',
          description: activity.description || '',
          timeCommitment: activity.hours_per_week ? `${activity.hours_per_week} hrs/week` : '',
          yearsInvolved: activity.years_participated || '',
          achievements: activity.achievements || ''
        });
      });
    }
    
    // Add from leadership_activities field in profile
    if (profile?.leadership_activities) {
      const leadershipItems = Array.isArray(profile.leadership_activities)
        ? profile.leadership_activities
        : typeof profile.leadership_activities === 'string'
          ? profile.leadership_activities.split(',').map(s => s.trim())
          : [];
          
      leadershipItems.forEach((activity, index) => {
        if (activity && !extracurriculars.some(e => e.activity === activity)) {
          extracurriculars.push({
            id: 'leadership-' + index,
            activity: activity,
            role: 'Leadership Position',
            description: '',
            timeCommitment: '',
            yearsInvolved: '',
            achievements: ''
          });
        }
      });
    }
    
    // Add from extracurriculars field in profile if it exists
    if (profile?.extracurriculars) {
      const extraItems = Array.isArray(profile.extracurriculars)
        ? profile.extracurriculars
        : typeof profile.extracurriculars === 'string'
          ? profile.extracurriculars.split(',').map(s => s.trim())
          : [];
          
      extraItems.forEach((activity, index) => {
        if (activity && !extracurriculars.some(e => e.activity === activity)) {
          extracurriculars.push({
            id: 'extra-' + index,
            activity: activity,
            role: 'Participant',
            description: '',
            timeCommitment: profile.time_commitment || '',
            yearsInvolved: '',
            achievements: ''
          });
        }
      });
    }
    
    return extracurriculars;
  };

  // Helper function to extract personal qualities
  const extractPersonalQualities = (insights, profile, excludePersonalBrand = false) => {
    const qualities = [];
    
    // Extract from assessment superpowers
    if (insights?.superpowers) {
      insights.superpowers.forEach((superpower, index) => {
        if (typeof superpower === 'string') {
          qualities.push({
            id: 'superpower-' + index,
            quality: superpower.split(':')[0] || superpower,
            evidence: superpower.includes(':') ? superpower.split(':')[1] : ''
          });
        }
      });
    }
    
    // Add from strategic positioning (unless excluded)
    if (insights?.positioning?.brand && !excludePersonalBrand) {
      qualities.push({
        id: 'brand',
        quality: 'Personal Brand',
        evidence: insights.positioning.brand
      });
    }
    
    // Add first-generation status if applicable
    if (profile.first_generation_college) {
      qualities.push({
        id: 'first-gen',
        quality: 'First-Generation College Student',
        evidence: 'Breaking new ground as the first in family to pursue higher education'
      });
    }
    
    return qualities;
  };

  // Helper function to extract challenges overcome
  const extractChallengesOvercome = (profile) => {
    const challenges = [];
    
    // Add grade trend context if there was improvement
    if (profile.grade_trend === 'upward' && profile.grade_context) {
      challenges.push({
        id: 'grade-trend',
        challenge: 'Academic performance challenges',
        solution: 'Developed better study habits and time management skills',
        growth: profile.grade_context
      });
    }
    
    // Add any special circumstances
    if (profile.special_circumstances) {
      challenges.push({
        id: 'circumstances',
        challenge: 'Personal circumstances',
        solution: 'Persevered through challenging situations',
        growth: profile.special_circumstances
      });
    }
    
    return challenges;
  };

  // Helper formatting functions
  const formatGPA = (profile) => {
    if (profile.gpa_weighted && profile.gpa_unweighted) {
      return `${profile.gpa_weighted} weighted / ${profile.gpa_unweighted} unweighted`;
    }
    return profile.gpa_weighted || profile.gpa_unweighted || profile.gpa || '';
  };

  const formatClassRank = (profile) => {
    if (profile.class_rank && profile.class_size) {
      return `${profile.class_rank}/${profile.class_size}`;
    }
    return profile.class_rank || '';
  };

  const formatCourseLoad = (profile) => {
    const courses = [];
    if (profile.ap_courses_taken) courses.push(`${profile.ap_courses_taken} AP courses`);
    if (profile.honors_courses) courses.push(`${profile.honors_courses} Honors courses`);
    if (profile.dual_enrollment_credits) courses.push(`${profile.dual_enrollment_credits} Dual Enrollment credits`);
    
    if (courses.length === 0 && profile.curriculum_rigor) {
      return `${profile.curriculum_rigor} curriculum`;
    }
    
    return courses.join(', ') || profile.course_load || '';
  };

  const generateAcademicChallenges = (profile) => {
    if (profile.academic_challenges) return profile.academic_challenges;
    
    if (profile.grade_trend === 'upward') {
      return 'Demonstrated consistent improvement and academic resilience';
    }
    
    if (profile.hs_competitiveness === 'highly_competitive') {
      return 'Excelling in a highly competitive academic environment';
    }
    
    return '';
  };

  const extractFavoriteSubjects = (profile) => {
    if (profile.favorite_subjects) {
      return Array.isArray(profile.favorite_subjects) 
        ? profile.favorite_subjects 
        : typeof profile.favorite_subjects === 'string'
          ? profile.favorite_subjects.split(',').map(s => s.trim())
          : [];
    }
    
    if (profile.academic_interests) {
      return Array.isArray(profile.academic_interests)
        ? profile.academic_interests
        : typeof profile.academic_interests === 'string'
          ? profile.academic_interests.split(',').map(s => s.trim())
          : [];
    }
    
    return [];
  };

  const saveBragSheet = async () => {
    try {
      setSaving(true);
      console.log('💾 Manual save initiated');
      console.log('📍 Using profile ID:', profileId);

      if (profileId) {
        // Update existing profile
        const { error } = await supabase
          .from('student_academic_profiles')
          .update({
            brag_sheet_data: bragSheetData,
            brag_sheet_updated: new Date().toISOString()
          })
          .eq('id', profileId);

        if (error) throw error;
        console.log('✅ Brag sheet updated successfully for profile:', profileId);
      } else {
        // Create new profile if none exists
        const { data, error } = await supabase
          .from('student_academic_profiles')
          .insert({
            user_id: currentUser.id,
            brag_sheet_data: bragSheetData,
            brag_sheet_updated: new Date().toISOString()
          })
          .select('id')
          .single();

        if (error) throw error;

        // Store the new profile ID for future updates
        if (data?.id) {
          setProfileId(data.id);
          console.log('✅ New brag sheet profile created with ID:', data.id);
        }
      }
    } catch (error) {
      console.error('❌ Error saving brag sheet:', error);
      alert('There was an error saving your brag sheet. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Centralized auto-save function that safely updates only brag sheet data
  // Calculate completion score for progress bar
  const calculateCompletionScore = (data) => {
    if (!showProgressBar || !selectedTeacherId) return 0;

    let totalWeight = 0;
    let completedWeight = 0;

    // Basic Info (15 points)
    totalWeight += 15;
    const basicInfoFields = ['name', 'gradeLevel', 'school', 'intendedMajor'];
    const filledBasicInfo = basicInfoFields.filter(field => data.basicInfo?.[field]).length;
    completedWeight += (filledBasicInfo / basicInfoFields.length) * 15;

    // Academic Highlights (10 points)
    totalWeight += 10;
    const academicFields = ['gpa', 'classRank'];
    const filledAcademic = academicFields.filter(field => data.academicHighlights?.[field]).length;
    completedWeight += (filledAcademic / academicFields.length) * 10;

    // College Goals (15 points)
    totalWeight += 15;
    if (data.collegeGoals && data.collegeGoals.length > 50) {
      completedWeight += 15;
    } else if (data.collegeGoals && data.collegeGoals.length > 0) {
      completedWeight += 7;
    }

    // Classroom Moments (25 points) - Most important
    totalWeight += 25;
    const classroomMoments = data.classroomMoments || [];
    if (classroomMoments.length >= 3) {
      completedWeight += 25;
    } else if (classroomMoments.length >= 2) {
      completedWeight += 17;
    } else if (classroomMoments.length >= 1) {
      completedWeight += 10;
    }

    // Achievements (10 points)
    totalWeight += 10;
    const achievements = data.achievements || [];
    if (achievements.length >= 3) {
      completedWeight += 10;
    } else if (achievements.length > 0) {
      completedWeight += (achievements.length / 3) * 10;
    }

    // Unexpected Details (10 points)
    totalWeight += 10;
    const unexpectedDetails = data.unexpectedDetails || [];
    if (unexpectedDetails.length >= 2) {
      completedWeight += 10;
    } else if (unexpectedDetails.length >= 1) {
      completedWeight += 5;
    }

    // Thank You Note (15 points)
    totalWeight += 15;
    if (data.thankYouNote && data.thankYouNote.length > 100) {
      completedWeight += 15;
    } else if (data.thankYouNote && data.thankYouNote.length > 0) {
      completedWeight += 7;
    }

    const score = Math.round((completedWeight / totalWeight) * 100);
    return Math.min(score, 100);
  };

  const autoSaveBragSheetData = async (newBragSheetData, actionDescription = '') => {
    console.log(`🔧 Auto-save attempt: ${actionDescription}`);
    console.log(`🔧 Profile ID available:`, profileId);
    console.log(`🔧 Current user ID:`, currentUser?.id);

    // Update completion score if in progress bar mode
    if (showProgressBar && selectedTeacherId) {
      const newScore = calculateCompletionScore(newBragSheetData);
      setCompletionScore(newScore);

      // Update workflow state
      let newWorkflowState = workflowState;
      if (newScore >= 70 && workflowState === 'in_progress') {
        newWorkflowState = 'ready';
      } else if (newScore > 0 && workflowState === 'not_started') {
        newWorkflowState = 'in_progress';
      }
      setWorkflowState(newWorkflowState);
    }
    
    try {
      if (profileId) {
        console.log(`🔄 Attempting UPDATE on profile ID: ${profileId}`);
        
        // First, verify the profile exists
        const { data: existingProfile, error: checkError } = await supabase
          .from('student_academic_profiles')
          .select('id, user_id')
          .eq('id', profileId)
          .single();
          
        if (checkError || !existingProfile) {
          console.error(`❌ Profile ID ${profileId} not found:`, checkError);
          console.log(`🔄 Clearing invalid profile ID and falling back to insert`);
          setProfileId(null);
          return autoSaveBragSheetData(newBragSheetData, actionDescription); // Retry with insert
        }
        
        console.log(`✅ Profile exists, proceeding with update`);
        
        // Update existing profile with only the brag sheet fields
        const { error, data } = await supabase
          .from('student_academic_profiles')
          .update({
            brag_sheet_data: newBragSheetData,
            brag_sheet_updated: new Date().toISOString()
          })
          .eq('id', profileId)
          .select('id');

        if (error) {
          console.error(`❌ Update failed for profile ${profileId}:`, error);
          throw error;
        }
        
        console.log(`✅ Successfully updated profile ${profileId} for: ${actionDescription}`);
        console.log(`🔧 Update returned:`, data);
        
      } else {
        console.log(`🆕 No profile ID available, creating new profile`);
        
        // No existing profile - create new one with basic required fields
        const { data, error } = await supabase
          .from('student_academic_profiles')
          .insert({
            user_id: currentUser.id,
            brag_sheet_data: newBragSheetData,
            brag_sheet_updated: new Date().toISOString()
          })
          .select('id')
          .single();

        if (error) {
          console.error(`❌ Insert failed for ${actionDescription}:`, error);
          throw error;
        }

        // Store the new profile ID for future updates
        if (data?.id) {
          setProfileId(data.id);
          console.log(`✅ Created new profile ${data.id} for: ${actionDescription}`);
        }
      }
    } catch (error) {
      console.error(`💥 Auto-save completely failed for ${actionDescription}:`, error);
      // Don't throw - we don't want to break the UI if save fails
    }
  };

  const addItem = async (section, newItem) => {
    const newData = {
      ...bragSheetData,
      [section]: [...(bragSheetData[section] || []), newItem]
    };
    setBragSheetData(newData);

    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, `after adding ${section} item`);
  };

  const removeItem = async (section, index) => {
    const newData = {
      ...bragSheetData,
      [section]: bragSheetData[section].filter((_, i) => i !== index)
    };
    setBragSheetData(newData);
    
    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, `after removing ${section} item`);
  };

  const updateItem = async (section, index, updatedItem) => {
    const newData = {
      ...bragSheetData,
      [section]: bragSheetData[section].map((item, i) => i === index ? updatedItem : item)
    };
    setBragSheetData(newData);
    
    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, `after updating ${section} item`);
  };

  const updateBasicInfo = async (field, value) => {
    const newData = {
      ...bragSheetData,
      basicInfo: { ...bragSheetData.basicInfo, [field]: value }
    };
    setBragSheetData(newData);
    
    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, `after updating basic info ${field}`);
  };

  const updateAcademicHighlights = async (field, value) => {
    const newData = {
      ...bragSheetData,
      academicHighlights: { ...bragSheetData.academicHighlights, [field]: value }
    };
    setBragSheetData(newData);

    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, `after updating academic highlights ${field}`);
  };

  // Handler for teacher selection
  const handleTeacherSelection = (teacherId) => {
    setSelectedTeacherId(teacherId);
    if (teacherId) {
      // Enable teacher-specific tab when a teacher is selected
      setActiveTab('teacher-specific');
    }
  };

  // Auto-saving wrapper functions for Personal section
  const updateLeadershipContext = async (contextId, data) => {
    const newData = {
      ...bragSheetData,
      leadershipContexts: {
        ...bragSheetData.leadershipContexts,
        [contextId]: data
      }
    };
    setBragSheetData(newData);
    
    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, `after updating leadership context ${contextId}`);
  };

  const updateCharacterStories = async (stories) => {
    const newData = {
      ...bragSheetData,
      characterStories: stories
    };
    setBragSheetData(newData);

    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, 'after updating character stories');
  };

  const updatePersonalBrand = async (brand) => {
    const newData = {
      ...bragSheetData,
      personalBrand: brand
    };
    setBragSheetData(newData);

    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, 'after updating personal brand');
  };

  const generateRecommendationTopics = async () => {
    const topics = [];
    
    // Generate topics based on achievements and qualities
    if (bragSheetData.achievements.length > 0) {
      topics.push({
        topic: 'Academic Excellence',
        description: 'Highlight specific achievements and academic growth',
        suggestedEvidence: bragSheetData.achievements.filter(a => a.category === 'academic').map(a => a.title)
      });
    }

    if (bragSheetData.extracurriculars.length > 0) {
      topics.push({
        topic: 'Leadership & Initiative',
        description: 'Showcase leadership roles and self-directed projects',
        suggestedEvidence: bragSheetData.extracurriculars.filter(e => e.role && (e.role.includes('President') || e.role.includes('Captain') || e.role.includes('Leader'))).map(e => e.activity)
      });
    }

    if (bragSheetData.personalQualities.length > 0) {
      topics.push({
        topic: 'Character & Values',
        description: 'Demonstrate personal growth and core values',
        suggestedEvidence: bragSheetData.personalQualities.map(q => q.quality)
      });
    }

    const newData = {
      ...bragSheetData,
      recommendationTopics: topics
    };
    setBragSheetData(newData);
    
    // Auto-save using safe update method
    await autoSaveBragSheetData(newData, 'after generating recommendation topics');
  };


  const exportToPDF = () => {
    // Create a temporary container for the PDF
    const printContainer = document.createElement('div');
    printContainer.id = 'professional-pdf-print';
    printContainer.style.position = 'fixed';
    printContainer.style.top = '0';
    printContainer.style.left = '0';
    printContainer.style.width = '100%';
    printContainer.style.height = 'auto';
    printContainer.style.minHeight = '100%';
    printContainer.style.background = 'white';
    printContainer.style.zIndex = '9999';
    printContainer.style.overflow = 'visible';
    document.body.appendChild(printContainer);
    
    // Render the professional PDF component using modern React
    const root = createRoot(printContainer);

    try {
      root.render(
        <ProfessionalBragSheetView
          bragSheetData={bragSheetData}
          isTeacherSpecific={isTeacherMode || !!selectedTeacherId}
          teacherInfo={{
            name: teacherInfo.name || teachers.find(t => t.id === selectedTeacherId)?.recommender_name || 'Teacher Name',
            subject: teacherInfo.subject || teachers.find(t => t.id === selectedTeacherId)?.subject || 'Subject',
            email: teacherInfo.email || teachers.find(t => t.id === selectedTeacherId)?.recommender_email || '',
            deadline: 'TBD'
          }}
          studentInfo={{
            email: studentData?.family?.email || currentUser?.email || bragSheetData.basicInfo?.email,
            phone: studentData?.family?.phone || bragSheetData.basicInfo?.phone,
            location: studentData?.profile?.city_state || bragSheetData.basicInfo?.location
          }}
          compactMode={compactMode}
        />
      );

      // Add print-specific styles
      const style = document.createElement('style');
      style.innerHTML = `
        @media print {
          @page {
            size: letter;
            margin: 0.75in;
          }

          body {
            margin: 0;
            padding: 0;
          }

          body * {
            visibility: hidden;
          }

          #professional-pdf-print, #professional-pdf-print * {
            visibility: visible;
          }

          #professional-pdf-print {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }

          /* Ensure sections break cleanly */
          .section {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Force page breaks at designated spots */
          .page-break {
            page-break-before: always;
            break-before: page;
          }

          /* Keep headers with their content */
          h2, h3 {
            page-break-after: avoid;
            break-after: avoid;
          }
        }
      `;
      document.head.appendChild(style);

      // Print after a longer delay to ensure rendering with React 18
      setTimeout(() => {
        window.print();

        // Clean up after printing
        setTimeout(() => {
          try {
            root.unmount();
            if (document.body.contains(printContainer)) {
              document.body.removeChild(printContainer);
            }
            if (document.head.contains(style)) {
              document.head.removeChild(style);
            }
          } catch (cleanupError) {
            console.warn('Cleanup error during PDF export:', cleanupError);
          }
        }, 1000);
      }, 1000);
    } catch (error) {
      console.error('Error rendering PDF component:', error);
      // Cleanup on error
      try {
        root.unmount();
        if (document.body.contains(printContainer)) {
          document.body.removeChild(printContainer);
        }
      } catch (cleanupError) {
        console.warn('Cleanup error after PDF export error:', cleanupError);
      }
    }
  };

  const exportToDoc = async () => {
    try {
      // Prepare data for DOC export
      const studentName = bragSheetData.basicInfo?.name || studentData?.profile?.name || 'Student Name';
      const currentYear = new Date().getFullYear();
      const graduationYear = bragSheetData.basicInfo?.graduationYear || 
        studentData?.profile?.graduation_year || 
        (currentYear + (13 - parseInt(bragSheetData.basicInfo?.gradeLevel || '12')));
      const highSchool = bragSheetData.basicInfo?.school || studentData?.profile?.high_school_name || 'High School';
      const gradeLevel = bragSheetData.basicInfo?.gradeLevel || '12';
      
      // Create document sections
      const sections = [];
      
      // Professional Header
      sections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: studentName,
              bold: true,
              size: 32,
              font: 'Georgia',
            }),
          ],
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `${gradeLevel}th Grade • ${highSchool} • Class of ${graduationYear}`,
              size: 20,
              color: '7f8c8d',
              font: 'Georgia',
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Personal Brand Quote (if available)
      if (bragSheetData.personalBrand && !excludePersonalBrand) {
        sections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'In My Own Words',
                bold: true,
                size: 22,
                font: 'Georgia',
              }),
            ],
            spacing: { before: 300, after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `"${bragSheetData.personalBrand}"`,
                italics: true,
                size: 22,
                font: 'Georgia',
              }),
            ],
            spacing: { after: 400 },
          })
        );
      }

      // Academic Overview Section Header
      sections.push(
        new Paragraph({
          children: [new TextRun({ text: 'Academic Overview', bold: true, size: 24, font: 'Georgia' })],
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 300 },
        })
      );

      // Academic Stats in structured format
      if (bragSheetData.academicHighlights && Object.keys(bragSheetData.academicHighlights).length > 0) {
        const academicData = bragSheetData.academicHighlights;
        
        // GPA
        if (academicData.gpa) {
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: 'GPA: ', bold: true, size: 20 }),
              new TextRun({ text: academicData.gpa.split(' ')[0] || academicData.gpa, size: 20 }),
              new TextRun({ text: ' (Weighted, 4.0 scale)', size: 18, color: '666666' }),
            ],
            spacing: { after: 150 },
          }));
        }
        
        // Class Rank
        if (academicData.classRank) {
          const [rank, total] = academicData.classRank.split('/');
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: 'Class Rank: ', bold: true, size: 20 }),
              new TextRun({ text: rank || 'N/A', size: 20 }),
              new TextRun({ text: ` of ${total || 'class'}`, size: 18, color: '666666' }),
            ],
            spacing: { after: 150 },
          }));
        }
        
        // Test Scores
        if (academicData.testScores) {
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: 'Test Scores: ', bold: true, size: 20 }),
              new TextRun({ text: academicData.testScores, size: 20 }),
              new TextRun({ text: ' (SAT/ACT)', size: 18, color: '666666' }),
            ],
            spacing: { after: 150 },
          }));
        }
        
        // Course Load
        if (academicData.courseLoad) {
          const apCount = academicData.courseLoad.match(/(\d+)/)?.[0] || '0';
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: 'Advanced Courses: ', bold: true, size: 20 }),
              new TextRun({ text: `${apCount} AP/Honors courses`, size: 20 }),
            ],
            spacing: { after: 150 },
          }));
        }

        // Academic Focus
        if (bragSheetData.basicInfo?.intendedMajor) {
          sections.push(new Paragraph({
            children: [
              new TextRun({ text: 'Academic Focus: ', bold: true, size: 20 }),
              new TextRun({ text: bragSheetData.basicInfo.intendedMajor, size: 20 }),
            ],
            spacing: { after: 300 },
          }));
        }
      }

      // Extracurricular Activities
      if (bragSheetData.extracurriculars && bragSheetData.extracurriculars.length > 0) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: 'Extracurricular Activities', bold: true, size: 24, font: 'Georgia' })],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 },
          })
        );
        
        bragSheetData.extracurriculars.forEach((activity, index) => {
          const activityName = activity.name || activity.activity || 'Activity';
          const role = activity.role || activity.position || '';
          const description = activity.description || activity.details || activity.achievements || activity.impact || '';
          const yearsInvolved = activity.yearsInvolved || activity.yearsParticipated || '';
          const timeCommitment = activity.timeCommitment || '';
          const hoursPerWeek = activity.hoursPerWeek || '';
          const weeksPerYear = activity.weeksPerYear || '';
          
          // Activity title and role
          sections.push(
            new Paragraph({
              children: [
                new TextRun({ text: activityName, bold: true, size: 22, font: 'Georgia' }),
                ...(role ? [new TextRun({ text: ` - ${role}`, size: 20, italics: true, font: 'Georgia' })] : []),
              ],
              spacing: { before: 300, after: 100 },
            })
          );
          
          // Description
          if (description) {
            sections.push(new Paragraph({
              children: [new TextRun({ text: description, size: 19, font: 'Georgia' })],
              spacing: { after: 150 },
            }));
          }
          
          // Time commitment details
          const timeDetails = [];
          if (yearsInvolved) timeDetails.push(`${yearsInvolved} years`);
          if (timeCommitment) timeDetails.push(timeCommitment);
          if (hoursPerWeek) timeDetails.push(`${hoursPerWeek} hrs/week`);
          if (weeksPerYear) timeDetails.push(`${weeksPerYear} weeks/year`);
          
          if (timeDetails.length > 0) {
            sections.push(new Paragraph({
              children: [new TextRun({ 
                text: timeDetails.join(' • '), 
                size: 16, 
                italics: true, 
                color: '666666',
                font: 'Georgia'
              })],
              spacing: { after: 200 },
            }));
          }
        });
      }

      // College Goals Section
      if (bragSheetData.collegeGoals) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: 'College Goals', bold: true, size: 24, font: 'Georgia' })],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 },
          }),
          new Paragraph({
            children: [new TextRun({ text: bragSheetData.collegeGoals, size: 20, font: 'Georgia' })],
            spacing: { after: 400 },
          })
        );
      }

      // Leadership & Character Experiences
      if (bragSheetData.leadershipContexts && Object.keys(bragSheetData.leadershipContexts).length > 0) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: 'Leadership & Character Experiences', bold: true, size: 24, font: 'Georgia' })],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 },
          })
        );

        Object.entries(bragSheetData.leadershipContexts).forEach(([contextId, description]) => {
          if (description && description.trim()) {
            const contextLabel = contextId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            sections.push(
              new Paragraph({
                children: [new TextRun({ text: contextLabel, bold: true, size: 22, font: 'Georgia' })],
                spacing: { before: 300, after: 150 },
              }),
              new Paragraph({
                children: [new TextRun({ text: description, size: 20, font: 'Georgia' })],
                spacing: { after: 300 },
              })
            );
          }
        });
      }

      // Academic Recognition (Achievements)
      if (bragSheetData.achievements && bragSheetData.achievements.length > 0) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: 'Academic Recognition', bold: true, size: 24, font: 'Georgia' })],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 },
          })
        );
        
        bragSheetData.achievements.forEach((achievement, index) => {
          const title = achievement.title || achievement.name || achievement.achievement || 'Achievement';
          const description = achievement.description || achievement.details || '';
          
          sections.push(
            new Paragraph({
              children: [new TextRun({ text: `• ${title}`, bold: true, size: 20, font: 'Georgia' })],
              spacing: { before: 150, after: description ? 100 : 150 },
            })
          );
          
          if (description) {
            sections.push(new Paragraph({
              children: [new TextRun({ text: description, size: 18, font: 'Georgia' })],
              spacing: { after: 150 },
            }));
          }
        });
      }

      // Things You Might Not Know (Unexpected Details)
      if (bragSheetData.unexpectedDetails && bragSheetData.unexpectedDetails.length > 0) {
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: 'A few things you might not know about me', bold: true, size: 24, font: 'Georgia' })],
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 },
          })
        );

        bragSheetData.unexpectedDetails.forEach((detail, index) => {
          const title = detail.title || `Detail ${index + 1}`;
          const description = detail.description || detail;

          sections.push(
            new Paragraph({
              children: [new TextRun({ text: title, bold: true, size: 20, font: 'Georgia' })],
              spacing: { before: 200, after: 100 },
            }),
            new Paragraph({
              children: [new TextRun({ text: description, size: 18, font: 'Georgia' })],
              spacing: { after: 200 },
            })
          );
        });
      }

      // Contact Information Footer
      sections.push(
        new Paragraph({
          children: [new TextRun({ text: '', size: 20 })],
          spacing: { before: 800, after: 400 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: 'CONTACT INFORMATION', bold: true, size: 16, font: 'Georgia' }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        }),
        new Paragraph({
          children: [
            new TextRun({ text: 'Email: ', bold: true, size: 18, font: 'Georgia' }),
            new TextRun({ 
              text: bragSheetData.basicInfo?.email || studentData?.family?.email || currentUser?.email || 'student@email.com', 
              size: 18, 
              font: 'Georgia' 
            }),
            new TextRun({ text: '  •  Phone: ', bold: true, size: 18, font: 'Georgia' }),
            new TextRun({ 
              text: bragSheetData.basicInfo?.phone || studentData?.family?.phone || '(555) 000-0000', 
              size: 18, 
              font: 'Georgia' 
            }),
            new TextRun({ text: '  •  Location: ', bold: true, size: 18, font: 'Georgia' }),
            new TextRun({ 
              text: bragSheetData.basicInfo?.location || studentData?.profile?.city_state || 'City, State', 
              size: 18, 
              font: 'Georgia' 
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        })
      );

      // Create the document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: sections,
          },
        ],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const fileName = `${studentName.replace(/[^a-zA-Z0-9]/g, '_')}_BragSheet.docx`;
      saveAs(blob, fileName);
      
    } catch (error) {
      console.error('Error exporting to DOC:', error);
      alert('There was an error creating the Word document. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading your profile data...</p>
        </div>
      </div>
    );
  }

  // Function to manually refresh auto-population
  const handleRefreshAutoPopulate = () => {
    if (studentData) {
      const populatedData = generateComprehensiveBragSheet(
        studentData.profile,
        studentData.assessment,
        studentData.family,
        studentData.activities,
        excludePersonalBrand
      );
      
      // Track which fields were auto-populated
      const populatedFields = new Set();
      if (populatedData.basicInfo.name) populatedFields.add('name');
      if (populatedData.basicInfo.gradeLevel) populatedFields.add('gradeLevel');
      if (populatedData.basicInfo.school) populatedFields.add('school');
      if (populatedData.basicInfo.intendedMajor) populatedFields.add('intendedMajor');
      if (populatedData.basicInfo.careerGoals) populatedFields.add('careerGoals');
      if (populatedData.academicHighlights.gpa) populatedFields.add('gpa');
      if (populatedData.academicHighlights.classRank) populatedFields.add('classRank');
      if (populatedData.academicHighlights.courseLoad) populatedFields.add('courseLoad');
      if (populatedData.achievements.length > 0) populatedFields.add('achievements');
      if (populatedData.extracurriculars.length > 0) populatedFields.add('extracurriculars');
      if (populatedData.personalQualities.length > 0) populatedFields.add('personalQualities');
      if (populatedData.challengesOvercome.length > 0) populatedFields.add('challenges');
      
      setAutoPopulatedFields(populatedFields);
      setBragSheetData(populatedData);
    }
  };

  // Handle thematic coherence analysis
  const handleThematicAnalysis = async () => {
    if (!studentData?.profile?.intended_major) {
      alert('Please add your intended major to your student profile first');
      return;
    }

    // If we have cached analysis, show it and ask if they want to re-run
    if (thematicAnalysis && !analyzingTheme) {
      const reAnalyze = window.confirm('You have a saved analysis. Would you like to re-run the analysis with your current brag sheet content?');
      if (!reAnalyze) {
        setShowThematicReview(true);
        return;
      }
    }

    setAnalyzingTheme(true);
    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'analyze-thematic-coherence',
          bragSheetData,
          intendedMajor: studentData.profile.intended_major,
          academicInterests: studentData.profile.academic_interests || [],
          userId: currentUser?.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis) {
          setThematicAnalysis(data.analysis);
          setShowThematicReview(true);

          // Save analysis results to database
          try {
            await supabase
              .from('student_academic_profiles')
              .update({
                thematic_analysis_results: data.analysis,
                thematic_analysis_date: new Date().toISOString()
              })
              .eq('user_id', currentUser?.id);

            console.log('✅ Saved thematic analysis to database');
          } catch (dbError) {
            console.error('Failed to save analysis to database:', dbError);
            // Don't show error to user - analysis still works
          }
        } else {
          alert('Failed to analyze thematic coherence. Please try again.');
        }
      } else {
        const error = await response.json();
        console.error('Analysis error:', error);
        alert(`Failed to analyze: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error analyzing thematic coherence:', error);
      alert('An error occurred while analyzing. Please try again.');
    } finally {
      setAnalyzingTheme(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-20">
      {/* Enhanced Header with Conditional Progress Bar */}
      <div className={`bg-white border-b border-slate-900 ${showProgressBar && selectedTeacherId ? 'sticky top-0 z-30' : ''}`}>
        <div className="px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-azure-500 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Brag Sheet Builder</h1>
                <p className="text-slate-600">
                  {selectedTeacherId && teachers.find(t => t.id === selectedTeacherId)
                    ? `For ${teachers.find(t => t.id === selectedTeacherId)?.recommender_name}`
                    : 'Create professional recommendation materials'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                Auto-saved {saving ? 'saving...' : '2 minutes ago'}
              </div>

              <button
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2 border border-slate-900 text-slate-900 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>

              <button
                onClick={handleThematicAnalysis}
                disabled={analyzingTheme || !studentData?.profile?.intended_major}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-violet-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 relative"
                title={!studentData?.profile?.intended_major ? 'Add your intended major in your student profile first' : 'Analyze how well your brag sheet aligns with your academic goals'}
              >
                <Sparkles className="w-4 h-4" />
                {analyzingTheme ? 'Analyzing...' : (thematicAnalysis ? 'Review Analysis' : 'Review Thematic Strength')}
                {thematicAnalysis && !analyzingTheme && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white"></span>
                )}
              </button>

              <ShareButton
                title="Brag Sheet"
                url={window.location.href}
                description="Professional brag sheet for college recommendations"
              />

              <button className="p-2 border border-slate-900 text-slate-600 rounded-lg hover:bg-slate-50">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar for Teacher-Specific Mode */}
        {showProgressBar && selectedTeacherId && (
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-slate-900">
                    Progress: {completionScore}%
                  </span>
                  {completionScore >= 70 && (
                    <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full font-semibold animate-pulse">
                      🎉 Ready to continue!
                    </span>
                  )}
                  {completionScore >= 50 && completionScore < 70 && (
                    <span className="text-sm bg-gold-100 text-gold-700 px-3 py-1.5 rounded-full font-semibold">
                      Almost there - {70 - completionScore}% to go
                    </span>
                  )}
                  {completionScore < 50 && completionScore > 0 && (
                    <span className="text-sm bg-azure-100 text-azure-700 px-3 py-1.5 rounded-full font-medium">
                      Keep going! {70 - completionScore}% until ready
                    </span>
                  )}
                </div>
                {saving && (
                  <span className="text-sm text-stone-500 flex items-center gap-1">
                    <Save className="h-4 w-4 animate-pulse" />
                    Saving...
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-stone-200 rounded-full h-3 overflow-hidden mb-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    completionScore >= 70 ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
                    completionScore >= 50 ? 'bg-gradient-to-r from-gold-500 to-orange-500' :
                    'bg-gradient-to-r from-azure-400 to-blue-500'
                  }`}
                  style={{ width: `${completionScore}%` }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div>
                <button
                  onClick={() => navigate('/college/recommendations')}
                  className="px-5 py-2 bg-white border-2 border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-2 font-medium"
                >
                  <Save className="h-4 w-4" />
                  Save & Exit
                </button>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  <button onClick={exportToDoc} className="px-5 py-2 bg-white border-2 border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-2 font-medium">
                    <FileText className="h-4 w-4" />
                    Export to DOCX
                  </button>
                  <button onClick={exportToPDF} className="px-5 py-2 bg-white border-2 border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-2 font-medium">
                    <FileText className="h-4 w-4" />
                    Export to PDF
                  </button>
                  <button
                    onClick={() => {
                      const teacher = teachers.find(t => t.id === selectedTeacherId);
                      navigate('/college/recommendations/follow-up-note', {
                        state: {
                          recommendation: teacher,
                          recommendationId: selectedTeacherId,
                          bragSheetData: bragSheetData,
                          fromBragSheet: true
                        }
                      });
                    }}
                    className={`px-6 py-2.5 text-white rounded-lg transition-all flex items-center gap-2 font-semibold ${
                      completionScore >= 70
                        ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/30'
                        : 'bg-azure-300 cursor-not-allowed opacity-60'
                    }`}
                    disabled={completionScore < 70}
                    title={completionScore < 70 ? `Complete ${70 - completionScore}% more to continue` : 'Continue to generate email'}
                  >
                    Generate Accompanying Email
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">

          {/* Main Content */}
          <div className="lg:col-span-8">

            {/* Progress Overview */}
            <div className="bg-white rounded-xl border border-slate-900 p-6 mb-6">
              {/* Only show this progress bar in non-progress-bar mode */}
              {!showProgressBar && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">Completion Progress</h2>
                    <span className="text-sm text-slate-600">Overall: 71% complete</span>
                  </div>

                  <div className="bg-stone-100 rounded-full h-2 mb-6">
                    <div className="bg-azure-500 h-2 rounded-full" style={{ width: '71%' }}></div>
                  </div>
                </>
              )}

              <div className="flex items-center justify-center gap-2 sm:gap-4 lg:gap-6 border-b border-slate-200 -mx-6 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview', count: '3/4', color: 'azure', icon: FileText },
                  { id: 'academic', label: 'Academic', count: '4/4', color: 'teal', icon: BookOpen },
                  { id: 'activities', label: 'Activities', count: '7/9', color: 'indigo', icon: Users },
                  { id: 'personal', label: 'Personal', count: '2/5', color: 'coral', icon: Heart },
                  ...((isTeacherMode || selectedTeacherId) ? [{ id: 'teacher-specific', label: 'Teacher-Specific', count: '1/3', color: 'gold', icon: GraduationCap }] : [])
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex flex-col items-center px-3 sm:px-4 lg:px-6 py-3 sm:py-4 transition-all relative flex-shrink-0 ${
                        isActive ? 'text-' + tab.color + '-700' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <div className={`w-10 sm:w-12 h-10 sm:h-12 rounded-full flex items-center justify-center bg-${tab.color}-500 mb-1 sm:mb-2`}>
                        <Icon className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
                      </div>
                      <p className="text-xs sm:text-sm font-medium mb-0.5">{tab.label}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{tab.count}</p>
                      {isActive && (
                        <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-${tab.color}-500`}></div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tab Content */}
              <div className="pt-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <BasicInfoSection
                data={bragSheetData.basicInfo}
                onChange={updateBasicInfo}
                autoPopulatedFields={autoPopulatedFields}
              />

              <CollegeGoalsSection
                data={bragSheetData.collegeGoals}
                onChange={(value) => {
                  setBragSheetData(prev => ({...prev, collegeGoals: value}));
                  autoSaveBragSheetData({...bragSheetData, collegeGoals: value}, 'after updating college goals');
                }}
                excluded={bragSheetData.sectionExclusions?.collegeGoals}
                onToggleExclusion={(section, excluded) => {
                  const newExclusions = {...(bragSheetData.sectionExclusions || {}), [section]: excluded};
                  setBragSheetData(prev => ({...prev, sectionExclusions: newExclusions}));
                  autoSaveBragSheetData({...bragSheetData, sectionExclusions: newExclusions}, 'after toggling section exclusion');
                }}
                showQuickStart={true}
              />
            </div>
          )}

          {activeTab === 'academic' && (
            <div className="space-y-6">
              <AcademicHighlightsSection
                data={bragSheetData.academicHighlights}
                onChange={updateAcademicHighlights}
              />

              <SimplifiedAchievementsSection
                achievements={bragSheetData.achievements}
                onAdd={(achievement) => addItem('achievements', achievement)}
                onRemove={(index) => removeItem('achievements', index)}
                onUpdate={(index, achievement) => updateItem('achievements', index, achievement)}
                excluded={bragSheetData.sectionExclusions?.achievements}
                onToggleExclusion={(section, excluded) => {
                  const newExclusions = {...(bragSheetData.sectionExclusions || {}), [section]: excluded};
                  setBragSheetData(prev => ({...prev, sectionExclusions: newExclusions}));
                  autoSaveBragSheetData({...bragSheetData, sectionExclusions: newExclusions}, 'after toggling section exclusion');
                }}
                autoPopulated={autoPopulatedFields.has('achievements')}
              />
            </div>
          )}

          {activeTab === 'activities' && (
            <ExtracurricularsSection 
              extracurriculars={bragSheetData.extracurriculars}
              onAdd={(activity) => addItem('extracurriculars', activity)}
              onRemove={(index) => removeItem('extracurriculars', index)}
              onUpdate={(index, activity) => updateItem('extracurriculars', index, activity)}
              autoPopulated={autoPopulatedFields.has('extracurriculars')}
            />
          )}

          {activeTab === 'personal' && (
            <PersonalGuidedSection
              leadershipContexts={bragSheetData.leadershipContexts || {}}
              onUpdateLeadership={updateLeadershipContext}
              characterStories={bragSheetData.characterStories || []}
              onUpdateCharacter={updateCharacterStories}
              personalBrand={bragSheetData.personalBrand || ''}
              onUpdatePersonalBrand={updatePersonalBrand}
              unexpectedDetails={bragSheetData.unexpectedDetails || []}
              bragSheetData={bragSheetData}
              autoPopulatedFields={autoPopulatedFields}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              onUpdateItem={updateItem}
              sectionExclusions={bragSheetData.sectionExclusions || {}}
              onToggleExclusion={(section, excluded) => {
                const newExclusions = {...(bragSheetData.sectionExclusions || {}), [section]: excluded};
                setBragSheetData(prev => ({...prev, sectionExclusions: newExclusions}));
                autoSaveBragSheetData({...bragSheetData, sectionExclusions: newExclusions}, 'after toggling section exclusion');
              }}
            />
          )}

          {activeTab === 'teacher-specific' && (isTeacherMode || selectedTeacherId) && (
            <div className="space-y-6">
              {/* Teacher Info Header */}
              {selectedTeacherId && (
                <div className="bg-gold-50 border-l-4 border-gold-500 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <GraduationCap className="w-5 h-5 text-gold-700" />
                    <h3 className="font-semibold text-gold-900">
                      Teacher-Specific for {teachers.find(t => t.id === selectedTeacherId)?.recommender_name}
                    </h3>
                  </div>
                  <p className="text-sm text-gold-700">
                    {teachers.find(t => t.id === selectedTeacherId)?.subject}
                  </p>
                </div>
              )}

              <ClassroomMomentsSection
                moments={bragSheetData.classroomMoments || []}
                onAdd={(moment) => addItem('classroomMoments', moment)}
                onRemove={(index) => removeItem('classroomMoments', index)}
                onUpdate={(index, moment) => updateItem('classroomMoments', index, moment)}
                excluded={bragSheetData.sectionExclusions?.classroomMoments}
                onToggleExclusion={(section, excluded) => {
                  const newExclusions = {...(bragSheetData.sectionExclusions || {}), [section]: excluded};
                  setBragSheetData(prev => ({...prev, sectionExclusions: newExclusions}));
                  autoSaveBragSheetData({...bragSheetData, sectionExclusions: newExclusions}, 'after toggling section exclusion');
                }}
                showQuickStart={true}
              />
              <ThankYouNoteSection
                note={bragSheetData.thankYouNote || ''}
                onChange={(value) => {
                  setBragSheetData(prev => ({...prev, thankYouNote: value}));
                  autoSaveBragSheetData({...bragSheetData, thankYouNote: value}, 'after updating thank you note');
                }}
                excluded={bragSheetData.sectionExclusions?.thankYouNote}
                onToggleExclusion={(section, excluded) => {
                  const newExclusions = {...(bragSheetData.sectionExclusions || {}), [section]: excluded};
                  setBragSheetData(prev => ({...prev, sectionExclusions: newExclusions}));
                  autoSaveBragSheetData({...bragSheetData, sectionExclusions: newExclusions}, 'after toggling section exclusion');
                }}
                showQuickStart={true}
              />
            </div>
          )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-4 sm:space-y-6">
            {/* Student Overview Card */}
            <div className="bg-white rounded-xl border border-slate-900 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-azure-100 flex items-center justify-center">
                  <span className="text-azure-700 font-semibold text-lg">
                    {bragSheetData.basicInfo?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'ST'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{bragSheetData.basicInfo?.name || 'Student Name'}</h3>
                  <p className="text-sm text-slate-600">
                    {bragSheetData.basicInfo?.gradeLevel || 'Grade'} • {bragSheetData.basicInfo?.school || 'School'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                <div>
                  <span className="text-slate-600">GPA</span>
                  <p className="font-semibold text-slate-900">{bragSheetData.academicHighlights?.gpa?.split(' ')[0] || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-600">Class Rank</span>
                  <p className="font-semibold text-slate-900">
                    {bragSheetData.academicHighlights?.classRank ?
                      `${bragSheetData.academicHighlights.classRank.split('/')[0]} of ${bragSheetData.academicHighlights.classRank.split('/')[1]}` :
                      'N/A'
                    }
                  </p>
                </div>
                <div>
                  <span className="text-slate-600">Test Scores</span>
                  <p className="font-semibold text-slate-900">{bragSheetData.academicHighlights?.testScores || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-600">Activities</span>
                  <p className="font-semibold text-slate-900">{bragSheetData.extracurriculars?.length || 0}</p>
                </div>
              </div>
            </div>

            {/* Teacher-Specific Selector */}
            {!isTeacherMode && (
              <div className="bg-white rounded-xl border border-slate-900 p-6">
                <h3 className="font-semibold text-slate-900 mb-3">Make Teacher-Specific</h3>
                <p className="text-xs text-slate-600 mb-3">
                  Customize this brag sheet for a specific teacher's recommendation
                </p>
                {teachers.length > 0 ? (
                  <>
                    <select
                      value={selectedTeacherId || ''}
                      onChange={(e) => handleTeacherSelection(e.target.value || null)}
                      className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                    >
                      <option value="">Select a teacher...</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.recommender_name} - {teacher.subject}
                        </option>
                      ))}
                    </select>
                    {selectedTeacherId && (
                      <div className="mt-2 p-2 bg-gold-50 border border-gold-200 rounded-lg">
                        <p className="text-xs text-gold-700 flex items-center gap-2">
                          <GraduationCap className="w-3 h-3" />
                          Teacher-Specific tab is now available
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                    <p className="text-xs text-slate-600 mb-2">No teachers added yet</p>
                    <p className="text-xs text-slate-500">
                      Add teachers in College Journey to create teacher-specific brag sheets
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-900 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowPreview(true)}
                  className="w-full p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3"
                >
                  <Eye className="w-4 h-4 text-azure-500" />
                  <span className="text-sm font-medium">Preview Brag Sheet</span>
                </button>
                <button
                  onClick={exportToPDF}
                  className="w-full p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3"
                >
                  <Download className="w-4 h-4 text-teal-500" />
                  <span className="text-sm font-medium">Export PDF</span>
                </button>
                <ShareButton
                  itemType="brag_sheet"
                  itemId={currentUser?.id || currentUser?.auth_user_id}
                  itemTitle="Student Brag Sheet"
                  currentStatus="complete"
                  className="w-full p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3"
                >
                  <Share2 className="w-4 h-4 text-coral-500" />
                  <span className="text-sm font-medium">Share with Teachers</span>
                </ShareButton>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-xl border border-slate-900 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Recommendations</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-azure-50 rounded-lg border border-azure-200">
                  <Sparkles className="w-4 h-4 text-azure-500" />
                  <div>
                    <p className="text-sm font-medium text-azure-900">Add more activities</p>
                    <p className="text-xs text-azure-700">You have room for more activities</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gold-50 rounded-lg border border-gold-200">
                  <Award className="w-4 h-4 text-gold-500" />
                  <div>
                    <p className="text-sm font-medium text-gold-900">Highlight achievements</p>
                    <p className="text-xs text-gold-700">Consider adding more awards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Professional PDF Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-lg max-w-full sm:max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-b gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <h2 className="text-lg sm:text-xl font-semibold">Professional Brag Sheet Preview</h2>
                <label className="flex items-center gap-2 text-xs sm:text-sm">
                  <input
                    type="checkbox"
                    checked={compactMode}
                    onChange={(e) => setCompactMode(e.target.checked)}
                    className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
                  />
                  <span className="text-stone-600">Compact Mode (fit more per page)</span>
                </label>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={exportToPDF}
                  className="px-3 sm:px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center justify-center gap-2 text-sm flex-1 sm:flex-initial"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Export PDF</span>
                  <span className="sm:hidden">Export</span>
                </button>
                <button
                  onClick={exportToDoc}
                  className="px-3 sm:px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 flex items-center justify-center gap-2 text-sm flex-1 sm:flex-initial"
                >
                  <FileText className="h-4 w-4" />
                  Export DOC
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-8" style={{ backgroundColor: '#f5f5f5' }}>
              <ProfessionalBragSheetView
                ref={printRef}
                bragSheetData={bragSheetData}
                isTeacherSpecific={isTeacherMode || !!selectedTeacherId}
                teacherInfo={{
                  name: teacherInfo.name || teachers.find(t => t.id === selectedTeacherId)?.teacher_name || 'Teacher Name',
                  subject: teacherInfo.subject || teachers.find(t => t.id === selectedTeacherId)?.teacher_subject || 'Subject',
                  email: teacherInfo.email || teachers.find(t => t.id === selectedTeacherId)?.teacher_email || '',
                  deadline: 'TBD'
                }}
                studentInfo={{
                  email: studentData?.family?.email || currentUser?.email || bragSheetData.basicInfo?.email,
                  phone: studentData?.family?.phone || bragSheetData.basicInfo?.phone,
                  location: studentData?.profile?.city_state || bragSheetData.basicInfo?.location
                }}
                compactMode={compactMode}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sticky Action Footer */}
      {!previewMode && !isTeacherMode && !showProgressBar && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 px-6 py-4 z-40">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              {/* Progress indicator */}
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <span className="text-xs text-stone-600">Progress</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-stone-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-azure-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(() => {
                          // Calculate completion inline
                          let total = 0;
                          let completed = 0;

                          // Basic info
                          ['name', 'gradeLevel', 'school', 'intendedMajor'].forEach(field => {
                            total++;
                            if (bragSheetData.basicInfo?.[field]) completed++;
                          });

                          // Academic
                          ['gpa', 'classRank', 'courseLoad'].forEach(field => {
                            total++;
                            if (bragSheetData.academicHighlights?.[field]) completed++;
                          });

                          // Lists
                          total += 3;
                          if (bragSheetData.achievements?.length >= 2) completed++;
                          if (bragSheetData.extracurriculars?.length >= 2) completed++;
                          if (bragSheetData.collegeGoals?.trim()) completed++;

                          return Math.round((completed / total) * 100);
                        })()}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-stone-900">{(() => {
                      // Calculate completion inline
                      let total = 0;
                      let completed = 0;

                      ['name', 'gradeLevel', 'school', 'intendedMajor'].forEach(field => {
                        total++;
                        if (bragSheetData.basicInfo?.[field]) completed++;
                      });

                      ['gpa', 'classRank', 'courseLoad'].forEach(field => {
                        total++;
                        if (bragSheetData.academicHighlights?.[field]) completed++;
                      });

                      total += 3;
                      if (bragSheetData.achievements?.length >= 2) completed++;
                      if (bragSheetData.extracurriculars?.length >= 2) completed++;
                      if (bragSheetData.collegeGoals?.trim()) completed++;

                      return Math.round((completed / total) * 100);
                    })()}%</span>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    await saveBragSheet();
                    toast.success('Brag sheet saved successfully!');
                    navigate('/college');
                  }}
                  className="px-6 py-2.5 bg-white border border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save & Exit
                </button>

                <button
                  onClick={() => setShowPreview(true)}
                  className="px-6 py-2.5 bg-white border border-stone-300 text-slate-700 rounded-lg hover:bg-stone-50 transition-colors flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>

                <button
                  onClick={async () => {
                    await saveBragSheet();
                    const progress = (() => {
                      let total = 0;
                      let completed = 0;
                      ['name', 'gradeLevel', 'school', 'intendedMajor'].forEach(field => {
                        total++;
                        if (bragSheetData.basicInfo?.[field]) completed++;
                      });
                      ['gpa', 'classRank', 'courseLoad'].forEach(field => {
                        total++;
                        if (bragSheetData.academicHighlights?.[field]) completed++;
                      });
                      total += 3;
                      if (bragSheetData.achievements?.length >= 2) completed++;
                      if (bragSheetData.extracurriculars?.length >= 2) completed++;
                      if (bragSheetData.collegeGoals?.trim()) completed++;
                      return Math.round((completed / total) * 100);
                    })();

                    if (progress >= 60) {
                      toast.success('Brag sheet saved! Continue to recommendations.');
                      navigate('/college/recommendations');
                    } else {
                      toast.info(`Complete at least 60% of your brag sheet to continue (currently ${progress}%)`);
                    }
                  }}
                  className={`px-6 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                    (() => {
                      let total = 10;
                      let completed = 0;
                      ['name', 'gradeLevel', 'school', 'intendedMajor'].forEach(field => {
                        if (bragSheetData.basicInfo?.[field]) completed++;
                      });
                      ['gpa', 'classRank', 'courseLoad'].forEach(field => {
                        if (bragSheetData.academicHighlights?.[field]) completed++;
                      });
                      if (bragSheetData.achievements?.length >= 2) completed++;
                      if (bragSheetData.extracurriculars?.length >= 2) completed++;
                      if (bragSheetData.collegeGoals?.trim()) completed++;
                      return Math.round((completed / total) * 100) >= 60;
                    })()
                      ? 'bg-azure-600 text-white hover:bg-azure-700'
                      : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                  }`}
                >
                  <ArrowRight className="h-4 w-4" />
                  Continue to Recommendations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Thematic Review Modal */}
      {showThematicReview && thematicAnalysis && (
        <ThematicReviewModal
          analysis={thematicAnalysis}
          onClose={() => setShowThematicReview(false)}
          intendedMajor={studentData?.profile?.intended_major || 'your chosen field'}
        />
      )}
    </div>
  );
}

// Function to generate personal brand from student data
function generatePersonalBrand(bragSheetData) {
  const { extracurriculars, achievements, personalQualities, academicHighlights } = bragSheetData;

  // Extract key themes from activities
  const activityThemes = [];
  extracurriculars.forEach(activity => {
    if (activity.role?.toLowerCase().includes('president') || activity.role?.toLowerCase().includes('captain')) {
      activityThemes.push('leader');
    }
    if (activity.activity?.toLowerCase().includes('volunteer') || activity.activity?.toLowerCase().includes('service')) {
      activityThemes.push('community-focused');
    }
    if (activity.activity?.toLowerCase().includes('tech') || activity.activity?.toLowerCase().includes('computer')) {
      activityThemes.push('tech-savvy');
    }
    if (activity.activity?.toLowerCase().includes('art') || activity.activity?.toLowerCase().includes('music') || activity.activity?.toLowerCase().includes('theater')) {
      activityThemes.push('creative');
    }
    if (activity.activity?.toLowerCase().includes('science') || activity.activity?.toLowerCase().includes('research')) {
      activityThemes.push('analytical');
    }
    if (activity.activity?.toLowerCase().includes('sport') || activity.activity?.toLowerCase().includes('athletic')) {
      activityThemes.push('athletic');
    }
  });

  // Extract qualities
  const topQualities = personalQualities.slice(0, 2).map(q =>
    typeof q === 'string' ? q : (q.quality || '')
  ).filter(Boolean);

  // Build brand statement
  let brandStatement = '';

  // Add leadership angle if applicable
  const uniqueThemes = [...new Set(activityThemes)];
  if (uniqueThemes.includes('leader')) {
    brandStatement = 'A natural leader who ';
  } else if (uniqueThemes.length > 0) {
    brandStatement = `A ${uniqueThemes[0]} individual who `;
  } else {
    brandStatement = 'A dedicated student who ';
  }

  // Add qualities
  if (topQualities.length > 0) {
    brandStatement += `brings ${topQualities.join(' and ').toLowerCase()} to every endeavor`;
  } else {
    brandStatement += 'strives for excellence in all pursuits';
  }

  // Add achievement focus
  if (achievements.length > 0) {
    const achievementTypes = achievements.map(a => a.category).filter(Boolean);
    const uniqueTypes = [...new Set(achievementTypes)];
    if (uniqueTypes.includes('academic')) {
      brandStatement += ', with proven academic excellence';
    } else if (uniqueTypes.includes('leadership')) {
      brandStatement += ', with demonstrated leadership impact';
    } else if (uniqueTypes.length > 0) {
      brandStatement += `, with achievements in ${uniqueTypes[0]}`;
    }
  }

  // Add academic strength if exceptional
  if (academicHighlights.gpa && parseFloat(academicHighlights.gpa) >= 3.8) {
    brandStatement += ' and strong academic performance';
  }

  brandStatement += '.';

  return brandStatement;
}

// Basic Info Section Component
function BasicInfoSection({ data, onChange, autoPopulatedFields = new Set(), excluded = false, onToggleExclusion }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Basic Information</h2>
          {(autoPopulatedFields.has('name') || autoPopulatedFields.has('gradeLevel') || autoPopulatedFields.has('school')) && (
            <span className="ml-2 text-xs text-azure-600 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Auto-populated
            </span>
          )}
        </div>
        {onToggleExclusion && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={excluded}
              onChange={(e) => onToggleExclusion('basicInfo', e.target.checked)}
              className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
            />
            <span className="text-stone-600">Exclude from brag sheet</span>
          </label>
        )}
      </div>

      {!excluded && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="Enter your full name"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Grade Level</label>
          <select
            value={data.gradeLevel}
            onChange={(e) => {
              onChange('gradeLevel', e.target.value);
              // Auto-calculate graduation year based on grade level
              if (e.target.value) {
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth();
                // If it's before June, we're in the spring semester
                const academicYear = currentMonth < 6 ? currentYear : currentYear + 1;
                const yearsUntilGraduation = 13 - parseInt(e.target.value);
                const calculatedGradYear = academicYear + yearsUntilGraduation;
                onChange('graduationYear', calculatedGradYear.toString());
              }
            }}
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          >
            <option value="">Select grade</option>
            <option value="9">9th Grade</option>
            <option value="10">10th Grade</option>
            <option value="11">11th Grade</option>
            <option value="12">12th Grade</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">School</label>
          <input
            type="text"
            value={data.school}
            onChange={(e) => onChange('school', e.target.value)}
            placeholder="Your high school"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Intended Major</label>
          <input
            type="text"
            value={data.intendedMajor}
            onChange={(e) => onChange('intendedMajor', e.target.value)}
            placeholder="What do you want to study?"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Graduation Year</label>
          <input
            type="number"
            value={data.graduationYear}
            onChange={(e) => onChange('graduationYear', e.target.value)}
            placeholder="2025"
            min="2024"
            max="2030"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <p className="text-xs text-stone-500 mt-1">Auto-calculated from grade level (adjust if needed)</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
          <input
            type="text"
            value={data.location}
            onChange={(e) => onChange('location', e.target.value)}
            placeholder="City, State"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email"
            value={data.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="your.email@example.com"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
      </div>
        </>
      )}
    </div>
  );
}

// Academic Highlights Section Component
function AcademicHighlightsSection({ data, onChange }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="h-5 w-5" />
        <h2 className="text-xl font-semibold">Academic Highlights</h2>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">GPA</label>
          <input
            type="text"
            value={data.gpa}
            onChange={(e) => onChange('gpa', e.target.value)}
            placeholder="3.85"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Class Rank</label>
          <input
            type="text"
            value={data.classRank}
            onChange={(e) => onChange('classRank', e.target.value)}
            placeholder="15/250"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Course Load</label>
          <input
            type="text"
            value={data.courseLoad}
            onChange={(e) => onChange('courseLoad', e.target.value)}
            placeholder="5 AP courses"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Academic Challenges Overcome</label>
        <textarea
          value={data.academicChallenges}
          onChange={(e) => onChange('academicChallenges', e.target.value)}
          placeholder="Describe any academic obstacles you've overcome..."
          rows={3}
          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Favorite Subjects</label>
        <input
          type="text"
          value={Array.isArray(data.favoriteSubjects) ? data.favoriteSubjects.join(', ') : (data.favoriteSubjects || '')}
          onChange={(e) => {
            // Keep as string while typing to allow natural input
            onChange('favoriteSubjects', e.target.value);
          }}
          onBlur={(e) => {
            // Convert to array on blur - split by commas and clean up
            const value = e.target.value;
            if (value && value.trim()) {
              const subjects = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
              onChange('favoriteSubjects', subjects);
            } else {
              onChange('favoriteSubjects', []);
            }
          }}
          placeholder="Mathematics, Computer Science, Physics"
          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
        />
        <p className="text-xs text-stone-500 mt-1">Separate multiple subjects with commas</p>
      </div>
    </div>
  );
}

// Achievements Section Component
function AchievementsSection({ achievements, onAdd, onRemove, onUpdate, autoPopulated }) {
  const [newAchievement, setNewAchievement] = useState({
    title: '',
    category: 'academic',
    description: '',
    impact: '',
    date: ''
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [editingAchievement, setEditingAchievement] = useState(null);

  const handleAdd = () => {
    if (newAchievement.title.trim()) {
      onAdd({ ...newAchievement, id: Date.now() });
      setNewAchievement({
        title: '',
        category: 'academic',
        description: '',
        impact: '',
        date: ''
      });
    }
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditingAchievement({ ...achievements[index] });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingAchievement(null);
  };

  const saveEdit = () => {
    if (editingAchievement && editingAchievement.title.trim()) {
      onUpdate(editingIndex, editingAchievement);
      setEditingIndex(null);
      setEditingAchievement(null);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Award className="h-5 w-5" />
        <h2 className="text-xl font-semibold">Achievements & Awards</h2>
        {autoPopulated && (
          <span className="ml-2 text-xs text-azure-600 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Auto-populated from profile
          </span>
        )}
      </div>
      
      {/* List of existing achievements FIRST */}
      {achievements.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-slate-700">Your Achievements ({achievements.length})</h4>
          {achievements.map((achievement, index) => (
            <div key={achievement.id || index} className="p-4 border border-stone-200 rounded-lg">
              {editingIndex === index ? (
                // Edit mode
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <input
                      placeholder="Achievement title"
                      value={editingAchievement.title || ''}
                      onChange={(e) => setEditingAchievement(prev => ({ ...prev, title: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                    <select
                      value={editingAchievement.category || 'academic'}
                      onChange={(e) => setEditingAchievement(prev => ({ ...prev, category: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    >
                      <option value="academic">Academic</option>
                      <option value="athletic">Athletic</option>
                      <option value="artistic">Artistic</option>
                      <option value="leadership">Leadership</option>
                      <option value="community">Community Service</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <textarea
                    placeholder="Describe the achievement and its significance..."
                    value={editingAchievement.description || ''}
                    onChange={(e) => setEditingAchievement(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <textarea
                      placeholder="What impact did this have?"
                      value={editingAchievement.impact || ''}
                      onChange={(e) => setEditingAchievement(prev => ({ ...prev, impact: e.target.value }))}
                      rows={2}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                    <input
                      type="date"
                      value={editingAchievement.date || ''}
                      onChange={(e) => setEditingAchievement(prev => ({ ...prev, date: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 text-slate-600 border border-stone-300 rounded-md hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEdit}
                      className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700"
                    >
                      <Save className="h-4 w-4 inline mr-1" />
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{achievement.title}</h4>
                      <span className="px-2 py-1 bg-stone-100 text-slate-600 rounded text-xs">
                        {achievement.category}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(index)}
                        className="text-azure-600 hover:text-azure-800"
                        title="Edit achievement"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onRemove(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete achievement"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm mb-2">{achievement.description}</p>
                  {achievement.impact && (
                    <p className="text-azure-600 text-sm mb-2">
                      <strong>Impact:</strong> {achievement.impact}
                    </p>
                  )}
                  {achievement.date && (
                    <p className="text-stone-500 text-sm">{achievement.date}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Add new achievement SECOND */}
      <div className="p-4 border border-stone-200 rounded-lg bg-stone-50 space-y-4">
        <h4 className="font-medium">Add New Achievement</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <input
            placeholder="Achievement title"
            value={newAchievement.title}
            onChange={(e) => setNewAchievement(prev => ({ ...prev, title: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <select
            value={newAchievement.category}
            onChange={(e) => setNewAchievement(prev => ({ ...prev, category: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          >
            <option value="academic">Academic</option>
            <option value="athletic">Athletic</option>
            <option value="artistic">Artistic</option>
            <option value="leadership">Leadership</option>
            <option value="community">Community Service</option>
            <option value="other">Other</option>
          </select>
        </div>
        <textarea
          placeholder="Describe the achievement and its significance..."
          value={newAchievement.description}
          onChange={(e) => setNewAchievement(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <textarea
            placeholder="What impact did this have?"
            value={newAchievement.impact}
            onChange={(e) => setNewAchievement(prev => ({ ...prev, impact: e.target.value }))}
            rows={2}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <input
            type="date"
            value={newAchievement.date}
            onChange={(e) => setNewAchievement(prev => ({ ...prev, date: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Achievement
        </button>
      </div>

      {/* List of achievements */}
      <div className="space-y-4">
        {achievements.map((achievement, index) => (
          <div key={achievement.id || index} className="p-4 border border-stone-200 rounded-lg">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{achievement.title}</h4>
                <span className="px-2 py-1 bg-stone-100 text-slate-600 rounded text-xs">
                  {achievement.category}
                </span>
              </div>
              <button
                onClick={() => onRemove(index)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <p className="text-slate-600 text-sm mb-2">{achievement.description}</p>
            {achievement.impact && (
              <p className="text-azure-600 text-sm mb-2">
                <strong>Impact:</strong> {achievement.impact}
              </p>
            )}
            {achievement.date && (
              <p className="text-stone-500 text-sm">{achievement.date}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Simplified Achievements Section Component (Title + Year)
function SimplifiedAchievementsSection({ achievements, onAdd, onRemove, onUpdate, excluded = false, onToggleExclusion, autoPopulated = false }) {
  const [newAchievement, setNewAchievement] = useState({
    title: '',
    year: ''
  });

  const handleAdd = () => {
    if (newAchievement.title.trim() && newAchievement.year.trim()) {
      onAdd({ ...newAchievement, id: Date.now() });
      setNewAchievement({ title: '', year: '' });
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Achievements & Awards</h2>
          {autoPopulated && (
            <span className="flex items-center gap-1 text-sm text-azure-600">
              <Sparkles className="h-3 w-3" />
              Auto-populated
            </span>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('achievements', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <>
          <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
            <h4 className="font-medium mb-4">Add Achievement</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <input
                placeholder="Achievement title"
                value={newAchievement.title}
                onChange={(e) => setNewAchievement({...newAchievement, title: e.target.value})}
                className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <input
                placeholder="Year (e.g., 2024)"
                value={newAchievement.year}
                onChange={(e) => setNewAchievement({...newAchievement, year: e.target.value})}
                className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
            </div>
            <button
              onClick={handleAdd}
              disabled={!newAchievement.title.trim() || !newAchievement.year.trim()}
              className="mt-3 px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700 disabled:bg-stone-300"
            >
              Add Achievement
            </button>
          </div>

          <div className="space-y-3">
            {achievements.map((achievement, index) => (
              <div key={achievement.id || index} className="p-4 border border-stone-200 rounded-lg flex justify-between items-center">
                <div>
                  <h5 className="font-medium text-slate-900">{achievement.title}</h5>
                  <p className="text-sm text-stone-600">{achievement.year}</p>
                </div>
                <button
                  onClick={() => onRemove(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Extracurriculars Section Component
function ExtracurricularsSection({ extracurriculars, onAdd, onRemove, onUpdate, autoPopulatedFields }) {
  const [newActivity, setNewActivity] = useState({
    activity: '',
    role: '',
    description: '',
    timeCommitment: '',
    yearsInvolved: '',
    achievements: ''
  });
  
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);

  const handleAdd = () => {
    if (newActivity.activity.trim()) {
      onAdd({ ...newActivity, id: Date.now() });
      setNewActivity({
        activity: '',
        role: '',
        description: '',
        timeCommitment: '',
        yearsInvolved: '',
        achievements: ''
      });
    }
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditingActivity({ ...extracurriculars[index] });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingActivity(null);
  };

  const saveEdit = () => {
    if (editingActivity && editingActivity.activity.trim()) {
      onUpdate(editingIndex, editingActivity);
      setEditingIndex(null);
      setEditingActivity(null);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5" />
        <h2 className="text-xl font-semibold">Extracurricular Activities</h2>
        {autoPopulatedFields?.has('extracurriculars') && (
          <span className="ml-2 text-xs text-azure-600 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Auto-populated from activities
          </span>
        )}
      </div>
      
      {/* List of existing activities FIRST */}
      {extracurriculars.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-slate-700">Your Activities ({extracurriculars.length})</h4>
          {extracurriculars.map((activity, index) => (
            <div key={activity.id || index} className="p-4 border border-stone-200 rounded-lg">
              {editingIndex === index ? (
                // Edit mode
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <input
                      placeholder="Activity name"
                      value={editingActivity.activity || ''}
                      onChange={(e) => setEditingActivity(prev => ({ ...prev, activity: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                    <input
                      placeholder="Your role/position"
                      value={editingActivity.role || ''}
                      onChange={(e) => setEditingActivity(prev => ({ ...prev, role: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                  </div>
                  <textarea
                    placeholder="Describe your involvement and contributions..."
                    value={editingActivity.description || ''}
                    onChange={(e) => setEditingActivity(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500 h-24"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <input
                      placeholder="Time commitment (e.g. 5 hrs/week)"
                      value={editingActivity.timeCommitment || ''}
                      onChange={(e) => setEditingActivity(prev => ({ ...prev, timeCommitment: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                    <input
                      placeholder="Years involved"
                      value={editingActivity.yearsInvolved || ''}
                      onChange={(e) => setEditingActivity(prev => ({ ...prev, yearsInvolved: e.target.value }))}
                      className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                  </div>
                  <textarea
                    placeholder="Notable achievements or accomplishments..."
                    value={editingActivity.achievements || ''}
                    onChange={(e) => setEditingActivity(prev => ({ ...prev, achievements: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500 h-20"
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 text-slate-600 border border-stone-300 rounded-md hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEdit}
                      className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700"
                    >
                      <Save className="h-4 w-4 inline mr-1" />
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium">{activity.activity}</h4>
                      <p className="text-azure-600 text-sm">{activity.role}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(index)}
                        className="text-azure-600 hover:text-azure-800"
                        title="Edit activity"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onRemove(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete activity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {activity.description && (
                    <p className="text-slate-600 text-sm mb-2">{activity.description}</p>
                  )}
                  <div className="flex gap-4 text-sm text-stone-500">
                    {activity.timeCommitment && <span>{activity.timeCommitment}</span>}
                    {activity.yearsInvolved && <span>{activity.yearsInvolved} years</span>}
                  </div>
                  {activity.achievements && (
                    <p className="text-emerald-600 text-sm mt-2">
                      <strong>Achievements:</strong> {activity.achievements}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new activity SECOND */}
      <div className="p-4 border border-stone-200 rounded-lg bg-stone-50 space-y-4">
        <h4 className="font-medium">Add New Activity</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <input
            placeholder="Activity name"
            value={newActivity.activity}
            onChange={(e) => setNewActivity(prev => ({ ...prev, activity: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <input
            placeholder="Your role/position"
            value={newActivity.role}
            onChange={(e) => setNewActivity(prev => ({ ...prev, role: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <textarea
          placeholder="Describe your involvement and contributions..."
          value={newActivity.description}
          onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <input
            placeholder="Time commitment (e.g., 10 hrs/week)"
            value={newActivity.timeCommitment}
            onChange={(e) => setNewActivity(prev => ({ ...prev, timeCommitment: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <input
            placeholder="Years involved"
            value={newActivity.yearsInvolved}
            onChange={(e) => setNewActivity(prev => ({ ...prev, yearsInvolved: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
        </div>
        <textarea
          placeholder="Key achievements or recognitions..."
          value={newActivity.achievements}
          onChange={(e) => setNewActivity(prev => ({ ...prev, achievements: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Activity
        </button>
      </div>
    </div>
  );
}

// Personal tab with dynamic guided sections
function PersonalGuidedSection({
  leadershipContexts = {},
  onUpdateLeadership,
  personalBrand = '',
  onUpdatePersonalBrand,
  unexpectedDetails = [],
  bragSheetData = {},
  onAddItem,
  onRemoveItem,
  onUpdateItem,
  sectionExclusions = {},
  onToggleExclusion
}) {
  // Leadership context options with their metadata
  const leadershipOptions = [
    {
      id: 'student-gov',
      label: 'Student Government',
      description: 'Class officer, student council, etc.',
      icon: 'SG',
      question: 'What made your student government leadership distinctive or impactful?',
      approaches: [
        {
          id: 'accomplishment',
          label: 'Specific accomplishment',
          guidance: 'Focus on one concrete thing you achieved or tried to change through student government',
          placeholder: 'I led the initiative to extend library hours during finals week, which required presenting usage data to administration and coordinating with facilities staff...'
        },
        {
          id: 'challenge',
          label: 'Challenge overcome',
          guidance: 'Focus on a difficult situation you navigated as a student leader and what you learned',
          placeholder: 'When student council was split on the dress code issue, I organized small group discussions to find common ground between different viewpoints...'
        },
        {
          id: 'approach',
          label: 'Leadership approach',
          guidance: 'Focus on what made your approach to student government different from previous leaders',
          placeholder: 'Unlike previous student body presidents who focused on events, I concentrated on policy changes that would outlast my term...'
        }
      ]
    },
    {
      id: 'athletics',
      label: 'Athletics',
      description: 'Team captain, sports leadership',
      icon: 'AT',
      question: 'How did you demonstrate leadership in athletics? What made your contribution unique?',
      approaches: [
        {
          id: 'impact',
          label: 'Team impact',
          guidance: 'Focus on how you contributed to team success beyond your individual performance',
          placeholder: 'I organized weekly film sessions to help younger players understand strategy, which improved our overall team performance by 15%...'
        },
        {
          id: 'adversity',
          label: 'Overcoming adversity',
          guidance: 'Focus on how you handled a challenging situation as a team leader',
          placeholder: 'When our star player was injured mid-season, I helped reorganize our strategy and kept team morale up during a difficult losing streak...'
        },
        {
          id: 'skills',
          label: 'Leadership skills',
          guidance: 'Focus on what athletics taught you about leadership that other activities couldn\'t',
          placeholder: 'Sports taught me how to motivate people under pressure and make quick decisions when stakes are high...'
        }
      ]
    },
    {
      id: 'service',
      label: 'Service Leadership',
      description: 'Community service, volunteer coordination',
      icon: 'SL',
      question: 'What made your community service leadership meaningful and effective?',
      approaches: [
        {
          id: 'project',
          label: 'Project impact',
          guidance: 'Focus on a specific service project you organized, improved, or expanded',
          placeholder: 'I reorganized our food drive to include nutritional education, which doubled participation and created lasting relationships with recipient families...'
        },
        {
          id: 'connection',
          label: 'Community connection',
          guidance: 'Focus on how you built genuine relationships with the community you served',
          placeholder: 'I spent time listening to community center staff to understand their actual needs rather than imposing our ideas about what help they wanted...'
        },
        {
          id: 'coordination',
          label: 'Team coordination',
          guidance: 'Focus on how you motivated and managed other volunteers over time',
          placeholder: 'I learned to match volunteers with roles that fit their interests and schedules, which dramatically reduced our dropout rate...'
        }
      ]
    },
    {
      id: 'academic',
      label: 'Academic',
      description: 'Club officer, honor society, etc.',
      icon: 'AL',
      question: 'How did you demonstrate leadership in academic clubs, honor societies, or educational activities?',
      approaches: [
        {
          id: 'building',
          label: 'Organization building',
          guidance: 'Focus on how you grew, improved, or transformed an academic organization',
          placeholder: 'I transformed debate club from a social group into a competitive team by establishing practice schedules and recruiting experienced coaches...'
        },
        {
          id: 'education',
          label: 'Peer education',
          guidance: 'Focus on how you created learning opportunities or brought students together academically',
          placeholder: 'I started peer tutoring sessions that created study communities rather than just one-on-one help...'
        },
        {
          id: 'culture',
          label: 'Academic culture',
          guidance: 'Focus on how you promoted academic excellence or intellectual curiosity',
          placeholder: 'I established a student research symposium where peers could present independent projects and learn from each other...'
        }
      ]
    },
    {
      id: 'workplace',
      label: 'Workplace',
      description: 'Job, internship, business leadership',
      icon: 'WP',
      question: 'What leadership experience or professional growth did you gain through work?',
      approaches: [
        {
          id: 'initiative',
          label: 'Professional initiative',
          guidance: 'Focus on a specific improvement or project you led at work',
          placeholder: 'I redesigned the inventory system at my retail job, which reduced stockouts by 30% and saved hours of manual tracking time...'
        },
        {
          id: 'collaboration',
          label: 'Team collaboration',
          guidance: 'Focus on how you worked with colleagues or improved workplace dynamics',
          placeholder: 'I mediated scheduling conflicts among part-time staff and created a fair rotation system that improved workplace morale...'
        },
        {
          id: 'skills',
          label: 'Skills development',
          guidance: 'Focus on what workplace leadership taught you that school activities couldn\'t',
          placeholder: 'Managing real business deadlines showed me how to prioritize when everything seems urgent and resources are limited...'
        }
      ]
    },
    {
      id: 'creative',
      label: 'Creative/Arts',
      description: 'Theater, music, art leadership',
      icon: 'CA',
      question: 'How did you demonstrate leadership in creative or artistic endeavors?',
      approaches: [
        {
          id: 'project',
          label: 'Creative project',
          guidance: 'Focus on a specific creative project you organized or led',
          placeholder: 'I directed our school\'s spring musical, coordinating rehearsals, technical crews, and budget constraints while maintaining creative vision...'
        },
        {
          id: 'collaboration',
          label: 'Collaboration',
          guidance: 'Focus on how you managed artistic personalities and balanced individual expression with group goals',
          placeholder: 'I coordinated between musicians, visual artists, and technical crews for our multimedia performance, ensuring everyone\'s contributions fit together...'
        },
        {
          id: 'advocacy',
          label: 'Arts advocacy',
          guidance: 'Focus on how you promoted arts education or made creative opportunities more accessible',
          placeholder: 'I established partnerships with local galleries to provide real-world performance opportunities for student artists...'
        }
      ]
    },
    {
      id: 'personal',
      label: 'Personal/Family',
      description: 'Family responsibilities, personal challenges',
      icon: 'PF',
      question: 'What leadership or significant responsibility have you taken on within your family?',
      approaches: [
        {
          id: 'responsibility',
          label: 'Family responsibility',
          guidance: 'Focus on a specific role or responsibility you\'ve taken on to help your family',
          placeholder: 'I manage the household budget and grocery shopping for my family of five, which taught me financial planning and time management...'
        },
        {
          id: 'support',
          label: 'Supporting others',
          guidance: 'Focus on how you\'ve helped family members through challenges or transitions',
          placeholder: 'I tutored my younger siblings through remote learning during COVID, creating structured schedules and helping with technical issues...'
        },
        {
          id: 'growth',
          label: 'Personal growth',
          guidance: 'Focus on how family responsibilities have shaped your character or skills',
          placeholder: 'Caring for my elderly grandmother while my parents work taught me patience, empathy, and how to advocate for someone else\'s needs...'
        }
      ]
    },
    {
      id: 'other',
      label: 'Other',
      description: 'Unique leadership experiences',
      icon: 'OT',
      question: 'Describe any other leadership experience that doesn\'t fit the above categories.',
      approaches: [
        {
          id: 'context',
          label: 'Unique context',
          guidance: 'Focus on the specific context or situation where you demonstrated leadership',
          placeholder: 'I organized neighborhood watch meetings after break-ins increased, which brought together 20+ families to coordinate safety measures...'
        },
        {
          id: 'skills',
          label: 'Skills gained',
          guidance: 'Focus on what specific skills or insights you developed through this experience',
          placeholder: 'Leading our church youth group\'s mission trip taught me how to coordinate logistics while being sensitive to cultural differences...'
        },
        {
          id: 'impact',
          label: 'Impact made',
          guidance: 'Focus on the concrete difference your leadership made in this unique context',
          placeholder: 'I started a community garden that now provides fresh produce for 15 families and created connections between neighbors who never spoke before...'
        }
      ]
    }
  ];

  // Initialize state from passed props
  const [selectedContexts, setSelectedContexts] = useState(() => {
    return new Set(Object.keys(leadershipContexts));
  });
  const [contextData, setContextData] = useState(() => {
    // Initialize with existing data or empty objects for each context
    const initialData = {};
    Object.keys(leadershipContexts).forEach(contextId => {
      initialData[contextId] = leadershipContexts[contextId] || '';
    });
    return initialData;
  });
  const [activeApproaches, setActiveApproaches] = useState(() => {
    // Initialize approach tabs to first option for each context
    const initialApproaches = {};
    Object.keys(leadershipContexts).forEach(contextId => {
      const context = leadershipOptions.find(opt => opt.id === contextId);
      if (context && context.approaches) {
        initialApproaches[contextId] = context.approaches[0].id;
      }
    });
    return initialApproaches;
  });

  const toggleContext = (contextId) => {
    const newSelected = new Set(selectedContexts);
    if (newSelected.has(contextId)) {
      newSelected.delete(contextId);
      // Clear data when deselected
      const newData = { ...contextData };
      delete newData[contextId];
      setContextData(newData);
    } else {
      newSelected.add(contextId);
      // Set default approach
      setActiveApproaches(prev => ({
        ...prev,
        [contextId]: leadershipOptions.find(opt => opt.id === contextId)?.approaches[0].id
      }));
    }
    setSelectedContexts(newSelected);
  };

  const updateContextData = (contextId, value) => {
    setContextData(prev => ({
      ...prev,
      [contextId]: value
    }));
    // Propagate to parent
    onUpdateLeadership(contextId, value);
  };

  const setActiveApproach = (contextId, approachId) => {
    setActiveApproaches(prev => ({
      ...prev,
      [contextId]: approachId
    }));
  };

  // State for personal brand
  const [localPersonalBrand, setLocalPersonalBrand] = useState(personalBrand || '');
  const [brandGenerated, setBrandGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGeneratePersonalBrand = async () => {
    setIsGenerating(true);
    try {
      // First try the API endpoint using the suggestions API with action parameter
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'generate-personal-brand',
          bragSheetData
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.personalBrand) {
          setLocalPersonalBrand(data.personalBrand);
          setBrandGenerated(true);
          if (onUpdatePersonalBrand) {
            onUpdatePersonalBrand(data.personalBrand);
          }
        }
      } else {
        // Fallback to local generation if API fails
        console.log('API failed, using local generation');
        const generatedBrand = generatePersonalBrand(bragSheetData);
        setLocalPersonalBrand(generatedBrand);
        setBrandGenerated(true);
        if (onUpdatePersonalBrand) {
          onUpdatePersonalBrand(generatedBrand);
        }
      }
    } catch (error) {
      // Fallback to local generation on any error
      console.error('Error generating personal brand:', error);
      const generatedBrand = generatePersonalBrand(bragSheetData);
      setLocalPersonalBrand(generatedBrand);
      setBrandGenerated(true);
      if (onUpdatePersonalBrand) {
        onUpdatePersonalBrand(generatedBrand);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePersonalBrandChange = (value) => {
    setLocalPersonalBrand(value);
    if (onUpdatePersonalBrand) {
      onUpdatePersonalBrand(value);
    }
  };

  return (
    <div className="space-y-6">
      {/* Personal Brand Section */}
      <div className="bg-white border border-stone-200 rounded-lg p-6">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold">In My Own Words</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleGeneratePersonalBrand}
                disabled={isGenerating}
                className="px-3 py-1 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 disabled:bg-violet-400 disabled:cursor-not-allowed flex items-center gap-2"
                title="Generate a personal statement based on your activities and achievements"
              >
                <Sparkles className="h-4 w-4" />
                {isGenerating ? 'Generating...' : (brandGenerated ? 'Regenerate' : 'Generate')} Statement
              </button>
              {onToggleExclusion && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={sectionExclusions?.personalBrand || false}
                    onChange={(e) => onToggleExclusion('personalBrand', e.target.checked)}
                    className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
                  />
                  <span className="text-stone-600">Exclude</span>
                </label>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            A brief, authentic statement about who you are and what drives you - written in your own voice
          </p>
          <textarea
            value={localPersonalBrand}
            onChange={(e) => handlePersonalBrandChange(e.target.value)}
            placeholder="E.g., 'I'm passionate about using technology to solve real problems in my community. When I saw kids in my neighborhood struggling with remote learning, I started free coding workshops that eventually grew into a nonprofit serving 200+ students.'"
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 resize-vertical"
            rows={3}
          />
          {brandGenerated && (
            <div className="mt-2 space-y-1">
              <p className="text-xs text-violet-600 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                A jumping off point auto-generated from your profile (meant to be edited by you!)
              </p>
              <p className="text-xs text-stone-500 italic">
                Remember: This is just a starting point! "In My Own Words" should truly be written in YOUR words.
                Edit this to ensure it represents your authentic voice and gives your recommenders a more nuanced understanding of who you are - in your own words.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Leadership & Character Experiences Section */}
      <div className="bg-white border border-stone-200 rounded-lg p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-center flex-1">
              <h2 className="text-2xl font-semibold mb-2">Leadership & Character Experiences</h2>
              <p className="text-slate-600">Select your leadership contexts and explain what makes each one distinctive</p>
            </div>
            {onToggleExclusion && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sectionExclusions?.leadershipContexts || false}
                  onChange={(e) => onToggleExclusion('leadershipContexts', e.target.checked)}
                  className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
                />
                <span className="text-stone-600">Exclude</span>
              </label>
            )}
          </div>
        </div>

        {/* Context Selection Grid */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Which leadership contexts apply to you?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {leadershipOptions.map(option => (
              <label 
                key={option.id}
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                  selectedContexts.has(option.id) 
                    ? 'border-azure-500 bg-azure-50' 
                    : 'border-stone-200 hover:border-azure-300 hover:bg-stone-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedContexts.has(option.id)}
                  onChange={() => toggleContext(option.id)}
                  className="mt-1 mr-3 text-azure-600 focus:ring-azure-500"
                />
                <div className="flex-1">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-sm text-slate-600">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Dynamic Fields for Selected Contexts */}
        <div className="space-y-4">
          {selectedContexts.size === 0 ? (
            <div className="text-center py-8 text-stone-500 italic">
              Select leadership contexts above to add details about each one
            </div>
          ) : (
            Array.from(selectedContexts).map(contextId => {
              const context = leadershipOptions.find(opt => opt.id === contextId);
              const activeApproach = activeApproaches[contextId] || context.approaches[0].id;
              const currentApproach = context.approaches.find(app => app.id === activeApproach);
              
              return (
                <div key={contextId} className="border border-stone-200 rounded-lg p-5 bg-stone-50">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-azure-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                      {context.icon}
                    </div>
                    <h4 className="text-lg font-semibold">{context.label} Leadership</h4>
                  </div>
                  
                  <p className="text-slate-700 mb-4">{context.question}</p>
                  
                  {/* Approach Selection Tabs */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {context.approaches.map(approach => (
                      <button
                        key={approach.id}
                        onClick={() => setActiveApproach(contextId, approach.id)}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          activeApproach === approach.id
                            ? 'bg-azure-100 text-azure-700 border border-azure-300'
                            : 'bg-stone-100 text-slate-600 hover:bg-stone-200'
                        }`}
                      >
                        {approach.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Guidance Box */}
                  <div className="bg-azure-50 border border-azure-200 rounded-md p-3 mb-3">
                    <p className="text-sm text-azure-900">
                      <strong>Focus: </strong>{currentApproach.guidance}
                    </p>
                  </div>
                  
                  {/* Text Input */}
                  <div>
                    <textarea
                      className="w-full p-3 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500 resize-vertical"
                      rows={4}
                      placeholder={currentApproach.placeholder}
                      value={contextData[contextId] || ''}
                      onChange={(e) => updateContextData(contextId, e.target.value)}
                      maxLength={300}
                    />
                    <div className="text-right text-sm text-stone-500 mt-1">
                      {(contextData[contextId] || '').length} / 300 characters
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Things You Might Not Know Section */}
      <UnexpectedDetailsSection
        details={unexpectedDetails}
        onAdd={(detail) => onAddItem('unexpectedDetails', detail)}
        onRemove={(index) => onRemoveItem('unexpectedDetails', index)}
        onUpdate={(index, detail) => onUpdateItem('unexpectedDetails', index, detail)}
        excluded={sectionExclusions?.unexpectedDetails}
        onToggleExclusion={onToggleExclusion}
        showQuickStart={true}
      />
    </div>
  );
}

// Original Personal Qualities Section Component (keeping for backward compatibility)
function PersonalQualitiesSection({ qualities, challenges, onAddQuality, onAddChallenge, onRemoveQuality, onRemoveChallenge, autoPopulated }) {
  const [newQuality, setNewQuality] = useState({ quality: '', evidence: '' });
  const [newChallenge, setNewChallenge] = useState({ challenge: '', solution: '', growth: '' });
  
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Heart className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Personal Qualities & Character</h2>
          {autoPopulated && (
            <span className="flex items-center gap-1 text-sm text-azure-600">
              <Sparkles className="h-3 w-3" />
              Auto-populated
            </span>
          )}
        </div>
        
        <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
          <h4 className="font-medium mb-4">Add Personal Quality</h4>
          <div className="space-y-4">
            <input
              placeholder="Quality or trait (e.g., Perseverance, Creativity)"
              value={newQuality.quality}
              onChange={(e) => setNewQuality(prev => ({ ...prev, quality: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
            />
            <textarea
              placeholder="Provide specific evidence or examples..."
              value={newQuality.evidence}
              onChange={(e) => setNewQuality(prev => ({ ...prev, evidence: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
            />
            <button
              onClick={() => {
                if (newQuality.quality.trim()) {
                  onAddQuality({ ...newQuality, id: Date.now() });
                  setNewQuality({ quality: '', evidence: '' });
                }
              }}
              className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Quality
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {qualities.map((quality, index) => (
            <div key={quality.id || index} className="p-3 border border-stone-200 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h5 className="font-medium text-azure-600">{quality.quality}</h5>
                  <p className="text-slate-600 text-sm mt-1">{quality.evidence}</p>
                </div>
                <button
                  onClick={() => onRemoveQuality(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Challenges Overcome</h2>
          {autoPopulated && (
            <span className="flex items-center gap-1 text-sm text-azure-600">
              <Sparkles className="h-3 w-3" />
              Auto-populated
            </span>
          )}
        </div>
        
        <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
          <h4 className="font-medium mb-4">Add Challenge</h4>
          <div className="space-y-4">
            <textarea
              placeholder="Describe the challenge or obstacle..."
              value={newChallenge.challenge}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, challenge: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
            />
            <textarea
              placeholder="How did you address or overcome it?"
              value={newChallenge.solution}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, solution: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
            />
            <textarea
              placeholder="What did you learn or how did you grow?"
              value={newChallenge.growth}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, growth: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
            />
            <button
              onClick={() => {
                if (newChallenge.challenge.trim()) {
                  onAddChallenge({ ...newChallenge, id: Date.now() });
                  setNewChallenge({ challenge: '', solution: '', growth: '' });
                }
              }}
              className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Challenge
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div key={challenge.id || index} className="p-4 border border-stone-200 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h5 className="font-medium text-red-600">Challenge</h5>
                <button
                  onClick={() => onRemoveChallenge(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <p className="text-slate-600 text-sm mb-2">{challenge.challenge}</p>
              <p className="text-azure-600 text-sm mb-2">
                <strong>Solution:</strong> {challenge.solution}
              </p>
              <p className="text-emerald-600 text-sm">
                <strong>Growth:</strong> {challenge.growth}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// Preview Component
const BragSheetPreview = React.forwardRef(({ data }, ref) => {
  return (
    <div ref={ref} className="bg-white p-8 space-y-8 print:p-0">
      <style jsx global>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
      
      <div className="print-area">
        <div className="text-center border-b pb-4 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Student Brag Sheet</h1>
          <p className="text-slate-600 mt-1">{data.basicInfo.name}</p>
          <p className="text-stone-500 text-sm">
            {data.basicInfo.gradeLevel} • {data.basicInfo.school}
          </p>
        </div>

        <div className="space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div><strong>Intended Major:</strong> {data.basicInfo.intendedMajor}</div>
              <div><strong>GPA:</strong> {data.academicHighlights.gpa}</div>
              <div className="col-span-2"><strong>Career Goals:</strong> {data.basicInfo.careerGoals}</div>
            </div>
          </section>

          {data.achievements.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
                Key Achievements
              </h2>
              <div className="space-y-2">
                {data.achievements.map((achievement, index) => (
                  <div key={index} className="text-sm">
                    <div className="font-medium">{achievement.title}</div>
                    <div className="text-slate-600">{achievement.description}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.extracurriculars.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
                Extracurricular Activities
              </h2>
              <div className="space-y-2">
                {data.extracurriculars.map((activity, index) => (
                  <div key={index} className="text-sm">
                    <div className="font-medium">{activity.activity} - {activity.role}</div>
                    <div className="text-slate-600">{activity.description}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.personalQualities.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
                Personal Qualities
              </h2>
              <div className="space-y-2">
                {data.personalQualities.map((quality, index) => (
                  <div key={index} className="text-sm">
                    <div className="font-medium">{quality.quality}</div>
                    <div className="text-slate-600">{quality.evidence}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.challengesOvercome.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
                Challenges Overcome
              </h2>
              <div className="space-y-3">
                {data.challengesOvercome.map((challenge, index) => (
                  <div key={index} className="text-sm">
                    <div className="font-medium">Challenge: {challenge.challenge}</div>
                    <div className="text-slate-600">Solution: {challenge.solution}</div>
                    <div className="text-azure-600">Growth: {challenge.growth}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.leadershipContexts && Object.keys(data.leadershipContexts).length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
                Leadership & Character Experiences
              </h2>
              <div className="space-y-3">
                {Object.entries(data.leadershipContexts).map(([contextId, description], index) => {
                  if (!description || !description.trim()) return null;
                  // Find the context label from the options
                  const contextLabel = contextId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <div key={index} className="text-sm">
                      <div className="font-medium text-violet-600">{contextLabel}</div>
                      <div className="text-slate-700">{description}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {data.characterStories && data.characterStories.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 border-b border-stone-200 pb-1 mb-3">
                Character Development Stories
              </h2>
              <div className="space-y-2">
                {data.characterStories.map((story, index) => (
                  <div key={index} className="text-sm">
                    <div className="font-medium">{story.title || `Story ${index + 1}`}</div>
                    <div className="text-slate-600">{story.content || story}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

        </div>
      </div>
    </div>
  );
});

BragSheetPreview.displayName = 'BragSheetPreview';

// College Goals Section Component
function CollegeGoalsSection({ data, onChange, excluded = false, onToggleExclusion, showQuickStart = false }) {
  const quickStartPrompts = [
    {
      label: "Liberal Arts & Political Science",
      text: "I'm applying to mid-tier liberal arts colleges and state universities with strong political science programs. My goal is to study government and public policy, with hopes of eventually working in public service or political analysis. Target schools include Illinois Wesleyan, DePauw, University of Illinois, and similar institutions that value leadership experience and civic engagement."
    },
    {
      label: "Engineering & Innovation",
      text: "I'm targeting state universities and technical institutes with strong engineering programs, particularly those with co-op opportunities. My focus is on mechanical engineering with a sustainability emphasis. I'm looking at schools like Purdue, University of Wisconsin, and Iowa State that combine strong academics with hands-on learning experiences and industry partnerships."
    },
    {
      label: "Pre-Med & Research",
      text: "I'm applying to research universities with strong pre-med advising and undergraduate research opportunities. My goal is to major in biochemistry while gaining clinical experience through hospital volunteering and shadowing programs. Target schools include Northwestern, Washington University, and University of Michigan, which offer both rigorous academics and proximity to medical centers."
    }
  ];

  const handleQuickStart = (promptText) => {
    if (!data || !data.trim()) {
      onChange(promptText);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          <h2 className="text-xl font-semibold">College Goals</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('collegeGoals', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <div>
          {showQuickStart && (!data || !data.trim()) && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Quick Start - Choose a template:</p>
              <div className="flex flex-wrap gap-2">
                {quickStartPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickStart(prompt.text)}
                    className="px-3 py-1 text-xs bg-azure-100 text-azure-700 rounded-full hover:bg-azure-200 transition-colors"
                    title={prompt.text.substring(0, 100) + '...'}
                  >
                    {prompt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="block text-sm font-medium text-slate-700 mb-2">
            Explain your college goals and target schools
          </label>
          <textarea
            value={data || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="I'm applying to mid-tier liberal arts colleges and state universities with strong political science programs. My goal is to study government and public policy..."
            rows={4}
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <p className="text-xs text-stone-500 mt-1">
            Help your teacher understand your academic goals and the types of schools you're targeting.
          </p>
        </div>
      )}
    </div>
  );
}

// Classroom Moments Section Component
function ClassroomMomentsSection({ moments, onAdd, onRemove, onUpdate, excluded = false, onToggleExclusion, showQuickStart = false }) {
  const [newMoment, setNewMoment] = useState({ title: '', description: '' });

  const quickStartPrompts = [
    { title: "Project that challenged me", description: "Working on the AP Biology research project pushed me to think critically about experimental design and data analysis in ways I hadn't before..." },
    { title: "Discussion where I contributed", description: "During our debate on the ethics of genetic modification, I brought up perspectives from indigenous communities that shifted the entire conversation..." },
    { title: "Moment I showed growth", description: "At the beginning of the year, I struggled with public speaking, but by semester's end I was confidently leading class presentations..." },
    { title: "Time I helped classmates", description: "When several students were struggling with calculus concepts, I organized study groups and created visual explanations that helped them understand..." }
  ];

  const handleAdd = () => {
    if (newMoment.title.trim() && newMoment.description.trim()) {
      onAdd({ ...newMoment, id: Date.now() });
      setNewMoment({ title: '', description: '' });
    }
  };

  const handleQuickStart = (prompt) => {
    setNewMoment(prompt);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Specific Classroom Moments</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('classroomMoments', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <>
          {showQuickStart && newMoment.title === '' && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Quick Start:</p>
              <div className="flex flex-wrap gap-2">
                {quickStartPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickStart(prompt)}
                    className="px-3 py-1 text-xs bg-azure-100 text-azure-700 rounded-full hover:bg-azure-200 transition-colors"
                  >
                    {prompt.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
            <h4 className="font-medium mb-4">Add Classroom Moment</h4>
            <div className="space-y-4">
              <input
                placeholder="Moment title (e.g., 'The Constitution Unit (September)')"
                value={newMoment.title}
                onChange={(e) => setNewMoment({...newMoment, title: e.target.value})}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <textarea
                placeholder="Describe what happened and how you grew or contributed..."
                value={newMoment.description}
                onChange={(e) => setNewMoment({...newMoment, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <button
                onClick={handleAdd}
                disabled={!newMoment.title.trim() || !newMoment.description.trim()}
                className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700 disabled:bg-stone-300"
              >
                Add Moment
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {moments.map((moment, index) => (
              <div key={moment.id || index} className="p-4 border border-stone-200 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h5 className="font-medium">{moment.title}</h5>
                  <button
                    onClick={() => onRemove(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-slate-600">{moment.description}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-stone-500">
            Share 3-4 specific moments from this teacher's class that show your growth, contributions, or learning process.
          </p>
        </>
      )}
    </div>
  );
}

// Unexpected Details Section Component
function UnexpectedDetailsSection({ details, onAdd, onRemove, onUpdate, excluded = false, onToggleExclusion, showQuickStart = false }) {
  const [newDetail, setNewDetail] = useState({ title: '', description: '' });

  const quickStartPrompts = [
    { title: "I'm a first-generation college student", description: "Being the first in my family to navigate college applications has taught me independence and made me passionate about helping others in similar situations..." },
    { title: "I have a unique hobby/skill", description: "I restore vintage electronics as a hobby, which has taught me patience, problem-solving, and given me a deep appreciation for how technology has evolved..." },
    { title: "I overcome a personal challenge", description: "Living with dyslexia has shaped how I approach learning - I've developed creative study methods that I now teach to other students..." },
    { title: "I have an interesting family background", description: "Growing up with parents from two different cultures has given me a unique perspective on bridging differences and finding common ground..." }
  ];

  const handleAdd = () => {
    if (newDetail.title.trim() && newDetail.description.trim()) {
      onAdd({ ...newDetail, id: Date.now() });
      setNewDetail({ title: '', description: '' });
    }
  };

  const handleQuickStart = (prompt) => {
    setNewDetail(prompt);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Things You Might Not Know About Me</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('unexpectedDetails', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <>
          {showQuickStart && newDetail.title === '' && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Quick Start:</p>
              <div className="flex flex-wrap gap-2">
                {quickStartPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickStart(prompt)}
                    className="px-3 py-1 text-xs bg-violet-100 text-violet-700 rounded-full hover:bg-violet-200 transition-colors"
                  >
                    {prompt.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
            <h4 className="font-medium mb-4">Add Personal Detail</h4>
            <div className="space-y-4">
              <input
                placeholder="Detail title (e.g., 'I'm a first-generation college student')"
                value={newDetail.title}
                onChange={(e) => setNewDetail({...newDetail, title: e.target.value})}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <textarea
                placeholder="Brief explanation or story..."
                value={newDetail.description}
                onChange={(e) => setNewDetail({...newDetail, description: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
              <button
                onClick={handleAdd}
                disabled={!newDetail.title.trim() || !newDetail.description.trim()}
                className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700 disabled:bg-stone-300"
              >
                Add Detail
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.map((detail, index) => (
              <div key={detail.id || index} className="p-4 border border-stone-200 rounded-lg bg-stone-50">
                <div className="flex justify-between items-start mb-2">
                  <h5 className="font-medium text-sm">{detail.title}</h5>
                  <button
                    onClick={() => onRemove(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-xs text-slate-600">{detail.description}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-stone-500">
            Share interesting personal details that help your teacher write a more personal recommendation.
          </p>
        </>
      )}
    </div>
  );
}

// Thank You Note Section Component
function ThankYouNoteSection({ note, onChange, excluded = false, onToggleExclusion, teacherName = '', showQuickStart = false }) {
  const quickStartPrompts = [
    "Thank you for your mentorship throughout this year. Your guidance has helped me grow not just academically, but as a person...",
    "Your class changed my perspective on [subject]. The way you taught [specific topic] made me realize...",
    "I appreciate your support during [challenging time]. When I was struggling with [issue], you helped me...",
    "Your feedback helped me grow as a student and thinker. I especially valued when you taught me..."
  ];

  const handleQuickStart = (prompt) => {
    if (!note.trim()) {
      onChange(prompt);
    }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Thank You Note</h2>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => onToggleExclusion('thankYouNote', e.target.checked)}
            className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
          />
          <span className="text-stone-600">Exclude from brag sheet</span>
        </label>
      </div>

      {!excluded && (
        <div>
          {showQuickStart && !note.trim() && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Quick Start:</p>
              <div className="flex flex-wrap gap-2">
                {quickStartPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickStart(prompt)}
                    className="px-3 py-1 text-xs bg-coral-100 text-coral-700 rounded-full hover:bg-coral-200 transition-colors"
                  >
                    Option {index + 1}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="block text-sm font-medium text-slate-700 mb-2">
            Personal thank you message {teacherName && `to ${teacherName}`}
          </label>
          <textarea
            value={note}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Thank you for taking the time to write this recommendation${teacherName ? `, ${teacherName}` : ''}. Your class has been instrumental in helping me understand...`}
            rows={4}
            className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-azure-500"
          />
          <p className="text-xs text-stone-500 mt-1">
            Express genuine gratitude and mention specific ways this teacher has impacted you.
          </p>
        </div>
      )}
    </div>
  );
}